import {
  clearPortalSessionStorage,
  readActivePortalMode,
  readActiveStudentId,
  readAuthToken,
  resolveStudentIdCandidate,
} from "../utils/parentPortalAuth.js";
import {
  normalizeManagementRoleKey,
  normalizeSystemRoleValue,
  resolveSessionRoles,
} from "../utils/rbac.js";
import { API_BASE, buildApiUrl } from "../config/apiBase.js";
import {
  recordClientDiagnostic,
  resolveRoleCategory,
  resolveSafeOperation,
  resolveSafeRouteName,
} from "../utils/clientDiagnostics.js";
import {
  isSeedEnabled,
  seedBoosterScore,
  seedCalendarEvents,
  seedCommunicationMessages,
} from "../utils/seedData.js";
import { normalizeBoosterManagementStudentsSummaryResponse } from "../utils/boosterManagementSummary.js";
import { prepareStudentCreationCommand } from "../utils/studentAdmissionCommand.js";

const base = API_BASE;
const getToken = () => readAuthToken();
const SESSION_TIMEOUT_NOTICE_KEY = "erp_session_timeout_notice";
const SESSION_TIMEOUT_MESSAGE = "Your session ended. Please log in again.";
const activeSessionsCache = new Map();
let sessionCache = { token: "", portalMode: "", data: null };
let sessionPromise = null;
let sessionPromiseToken = "";
let sessionPromisePortalMode = "";
const inflightRequests = new Map();
const responseCache = new Map();
const authenticatedRequestControllers = new Set();
let authFailureState = { token: "", message: "", timestamp: 0 };
const API_NOTICE_KEY = "api_notice";
let apiNoticeState = { message: "", timestamp: 0 };
const LOGIN_REQUEST_TIMEOUT_MS = 15000;
const AUTH_REQUEST_TIMEOUT_MS = 15000;

export const resetErpAuthenticatedRequestState = () => {
  authenticatedRequestControllers.forEach((controller) => {
    if (!controller.signal.aborted) controller.abort();
  });
  authenticatedRequestControllers.clear();
  inflightRequests.clear();
  responseCache.clear();
  activeSessionsCache.clear();
  sessionCache = { token: "", portalMode: "", data: null };
  sessionPromise = null;
  sessionPromiseToken = "";
  sessionPromisePortalMode = "";
};

if (typeof window !== "undefined") {
  window.addEventListener(
    "erp-auth-cleared",
    resetErpAuthenticatedRequestState,
  );
}

const readAuthoritativeSession = () => {
  const session = sessionCache?.data;
  return session && typeof session === "object" ? session : {};
};

const readAuthoritativeSessionUser = () =>
  resolveSessionRoles(readAuthoritativeSession())?.user || {};

const resolveHasSectionsFlag = () => {
  const session = readAuthoritativeSession();
  const token =
    session?.school?.has_sections ?? session?.school?.hasSections ?? null;
  if (token === null || token === undefined) return true;
  return Boolean(token);
};

const resolveSchoolCodeHeader = () => {
  const session = readAuthoritativeSession();
  return String(session?.school?.code || "").trim();
};

const resolveAuthoritativeSessionRole = () => {
  const session = sessionCache?.data;
  if (!session || typeof session !== "object") return "";
  const { systemRole, managementRole } = resolveSessionRoles(session);
  const normalizedSystemRole = normalizeSystemRoleValue(systemRole);
  if (normalizedSystemRole === "SUPER_ADMIN") return "superadmin";
  if (normalizedSystemRole === "TEACHER") return "teacher";
  if (normalizedSystemRole === "STUDENT") return "student";
  if (normalizedSystemRole === "ADMIN") {
    return normalizeManagementRoleKey(managementRole) || "management";
  }
  return "";
};

const resolveSchoolIdHeader = () => {
  const session = readAuthoritativeSession();
  return String(session?.school?.id || session?.rbac?.school_id || "").trim();
};

const isFinancePath = (path = "") =>
  /(^|\/)(fees?|receipt|payroll)(\/|$)/i.test(String(path));

const resolvePathname = (path = "") => {
  const rawPath = String(path || "");
  try {
    const parsed = new URL(rawPath, "http://localhost");
    return parsed.pathname || rawPath;
  } catch (error) {
    return rawPath;
  }
};

const isTeacherAllowedPayrollSelfServicePath = (path = "") => {
  const pathname = resolvePathname(path);
  return /\/teacher\/payroll\/(salary-statement|expense-claims|my-attendance|payslip)(\/|$)?/i.test(
    pathname,
  );
};

const assertFinanceAccess = (path = "", options = {}) => {
  const role = resolveAuthoritativeSessionRole();
  if (role === "teacher" && isFinancePath(path)) {
    if (isTeacherAllowedPayrollSelfServicePath(path)) return;
    throw new Error(
      "Access denied: finance modules are restricted for this role.",
    );
  }
};

const assertHomeworkCompletionWriteAccess = () => {
  const role = resolveAuthoritativeSessionRole();
  if (role === "teacher") {
    throw new Error(
      "Access denied: teachers have read-only access to homework completion.",
    );
  }
};

const normalizeHomeworkCompletionPayload = (payload = {}) => {
  const studentId = String(payload?.studentId || "").trim();
  const subjectId = String(payload?.subjectId || "").trim();
  const date = String(payload?.date || "").trim();
  if (!studentId || !subjectId || !date) {
    throw new Error("studentId, subjectId and date are required.");
  }
  return { studentId, subjectId, date };
};

const toSafeString = (value) => String(value ?? "").trim();

const normalizeStudentAccessToken = (value) =>
  toSafeString(value)
    .replace(/[^a-z0-9]/gi, "")
    .toLowerCase();

const hasMatchingStudentAccessToken = (allowedIds = [], requestedId = "") => {
  const requested = normalizeStudentAccessToken(requestedId);
  if (!requested) return false;
  return allowedIds.some(
    (allowedId) => normalizeStudentAccessToken(allowedId) === requested,
  );
};

const resolveStoredChildIds = () => {
  const stored = readAuthoritativeSessionUser();
  const activeStudentId = readActiveStudentId();
  const direct = [
    activeStudentId,
    stored?._id,
    stored?.ID,
    stored?.id,
    stored?.studentId,
    stored?.student_id,
    stored?.student_code,
    stored?.studentCode,
    stored?.login_id,
    stored?.loginId,
    stored?.rollno,
    stored?.childId,
    stored?.child_id,
    stored?.child?._id,
    stored?.child?.ID,
    stored?.child?.id,
    stored?.child?.studentId,
    stored?.child?.student_id,
    stored?.child?.student_code,
    stored?.child?.studentCode,
    stored?.child?.login_id,
    stored?.child?.loginId,
    stored?.child?.rollno,
  ];
  const fromChildren = Array.isArray(stored?.children)
    ? stored.children.flatMap((item) => [
        item?._id,
        item?.ID,
        item?.id,
        item?.studentId,
        item?.student_id,
        item?.student_code,
        item?.studentCode,
        item?.login_id,
        item?.loginId,
        item?.rollno,
      ])
    : [];
  return Array.from(
    new Set(
      [...direct, ...fromChildren]
        .map((item) => toSafeString(item))
        .filter(Boolean),
    ),
  );
};

const appendStudentIdCandidate = (collector, value) => {
  const next = toSafeString(value);
  if (next) collector.add(next);
};

const collectStudentIdsFromBody = (body, collector) => {
  const keys = [
    "studentId",
    "student_id",
    "childId",
    "child_id",
    "student",
    "child",
  ];
  if (!body) return;

  if (typeof body === "string") {
    try {
      const parsed = JSON.parse(body);
      collectStudentIdsFromBody(parsed, collector);
    } catch (error) {
      // ignore non-json string bodies
    }
    return;
  }

  if (typeof FormData !== "undefined" && body instanceof FormData) {
    keys.forEach((key) => appendStudentIdCandidate(collector, body.get(key)));
    return;
  }

  if (typeof body === "object") {
    keys.forEach((key) => appendStudentIdCandidate(collector, body?.[key]));
  }
};

const collectStudentIdsFromPath = (path = "", options = {}) => {
  const ids = new Set();
  const rawPath = String(path || "");
  let parsedUrl = null;
  try {
    parsedUrl = new URL(rawPath, "http://localhost");
  } catch (error) {
    parsedUrl = null;
  }

  if (parsedUrl) {
    ["studentId", "student_id", "childId", "child_id"].forEach((key) => {
      parsedUrl.searchParams
        .getAll(key)
        .forEach((value) => appendStudentIdCandidate(ids, value));
    });
  }

  const sourcePath = parsedUrl ? parsedUrl.pathname : rawPath;
  const pathMatchers = [
    /\/students\/[^/]+\/360\/([^/?#]+)/i,
    /\/fees\/student-ledger\/[^/]+\/([^/?#]+)/i,
    /\/fees\/reports\/student-statement\/[^/]+\/([^/?#]+)/i,
    /\/saveReceipt\/([^/?#]+)/i,
  ];
  pathMatchers.forEach((pattern) => {
    const match = sourcePath.match(pattern);
    if (match?.[1]) appendStudentIdCandidate(ids, decodeURIComponent(match[1]));
  });

  collectStudentIdsFromBody(options?.body, ids);
  return Array.from(ids);
};

const isParentBlockedPath = (path = "") => {
  const rawPath = String(path || "");
  let parsedUrl = null;
  try {
    parsedUrl = new URL(rawPath, "http://localhost");
  } catch (error) {
    parsedUrl = null;
  }
  const pathname = String(parsedUrl?.pathname || rawPath).toLowerCase();
  const formatValue = String(
    parsedUrl?.searchParams?.get("format") || "",
  ).toLowerCase();
  const downloadValue = String(
    parsedUrl?.searchParams?.get("download") || "",
  ).toLowerCase();

  if (formatValue === "csv" || downloadValue === "true") return true;
  if (/\/fees\/reports\//.test(pathname)) return true;
  if (/\/(export|download|print)(\/|$)/.test(pathname)) return true;
  if (
    /\/audit(?:[-_/]|$)/.test(pathname) ||
    /\/auditlog(?:[-_/]|$)/.test(pathname)
  )
    return true;
  return false;
};

const assertParentRestrictions = (path = "", options = {}) => {
  const role = resolveAuthoritativeSessionRole();
  if (role !== "student" && role !== "parent") return;

  if (isParentBlockedPath(path)) {
    throw new Error(
      "Access denied: exports, downloads, and audit logs are disabled for student role.",
    );
  }

  const ownChildIds = resolveStoredChildIds();
  if (!ownChildIds.length) return;
  const requestedIds = collectStudentIdsFromPath(path, options);
  if (!requestedIds.length) return;

  const allowed = requestedIds.every((requestedId) =>
    hasMatchingStudentAccessToken(ownChildIds, requestedId),
  );
  if (!allowed) {
    throw new Error(
      "Access denied: students can access only their own records.",
    );
  }
};

const withAuth = (options = {}) => {
  const headers = { ...(options.headers || {}) };
  const token = getToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const schoolId = resolveSchoolIdHeader();
  if (schoolId && !headers["x-school-id"] && !headers["X-School-Id"]) {
    headers["x-school-id"] = schoolId;
  }
  const schoolCode = resolveSchoolCodeHeader();
  if (
    !schoolId &&
    schoolCode &&
    !headers["x-school-code"] &&
    !headers["X-School-Code"]
  ) {
    headers["x-school-code"] = schoolCode;
  }
  const portalMode = readActivePortalMode();
  if (
    portalMode &&
    !headers["x-erp-portal-mode"] &&
    !headers["X-ERP-Portal-Mode"]
  ) {
    headers["x-erp-portal-mode"] = portalMode;
  }
  return { ...options, headers };
};

const shouldHandleAuthFailure = (token, message = "") => {
  const now = Date.now();
  const recent =
    authFailureState.message === message &&
    now - authFailureState.timestamp < 2000 &&
    (!token || authFailureState.token === token);
  if (recent) return false;
  authFailureState = { token, message, timestamp: now };
  return true;
};

const clearSessionAndRedirectToLogin = (notice = SESSION_TIMEOUT_MESSAGE) => {
  if (typeof window === "undefined") return;
  const token = getToken();
  if (!shouldHandleAuthFailure(token, notice)) return;
  try {
    clearPortalSessionStorage();
    localStorage.setItem(
      SESSION_TIMEOUT_NOTICE_KEY,
      String(notice || SESSION_TIMEOUT_MESSAGE),
    );
  } catch (error) {
    // no-op
  }
  const pathname = String(window.location?.pathname || "");
  if (pathname !== "/" && pathname !== "/login") {
    window.location.replace("/login");
  }
};

const isAuthReady = () => {
  if (typeof window === "undefined") return true;
  return Boolean(window.__erp_auth_ready);
};

const shouldNotifyApiNotice = (message = "") => {
  const now = Date.now();
  const recent =
    apiNoticeState.message === message && now - apiNoticeState.timestamp < 4000;
  if (recent) return false;
  apiNoticeState = { message, timestamp: now };
  return true;
};

const pushApiNotice = (payload = {}) => {
  if (typeof window === "undefined") return;
  const message = String(payload?.message || "").trim();
  if (!message) return;
  if (!shouldNotifyApiNotice(message)) return;
  const record = {
    message,
    actionLabel: payload?.actionLabel || "",
    action: payload?.action || "",
    tone: payload?.tone || "error",
    timestamp: Date.now(),
  };
  try {
    localStorage.setItem(API_NOTICE_KEY, JSON.stringify(record));
  } catch (error) {
    // ignore
  }
  try {
    window.dispatchEvent(new Event("app-notice"));
  } catch (error) {
    // ignore
  }
};

const AUDIT_STORAGE_KEY = "erp_audit_logs";
const AUDIT_MAX_ENTRIES = 500;
const AUDIT_PATH_PATTERNS = [
  /\/fees\b/i,
  /\/payroll\b/i,
  /\/api\/communication\b/i,
  /\/comms\b/i,
  /\/complaint\b/i,
  /\/students?\b/i,
  /\/teacher\b/i,
  /\/timetable\b/i,
  /\/approval\b/i,
  /\/admission\b/i,
  /\/inventory\b/i,
  /\/messages?\b/i,
];
const AUDIT_IGNORE_PATTERNS = [
  /\/login\b/i,
  /\/session\b/i,
  /\/refresh\b/i,
  /\/otp\b/i,
  /\/forgot\b/i,
  /\/reset\b/i,
  /\/upload\b/i,
];

const shouldAudit = (path = "", options = {}) => {
  const method = String(options?.method || "GET").toUpperCase();
  if (method === "GET") return false;
  if (AUDIT_IGNORE_PATTERNS.some((pattern) => pattern.test(path))) return false;
  return AUDIT_PATH_PATTERNS.some((pattern) => pattern.test(path));
};

const recordAuditEvent = (entry) => {
  if (typeof window === "undefined") return;
  try {
    const raw = localStorage.getItem(AUDIT_STORAGE_KEY);
    const existing = raw ? JSON.parse(raw) : [];
    const next = Array.isArray(existing) ? existing : [];
    next.unshift(entry);
    if (next.length > AUDIT_MAX_ENTRIES) next.length = AUDIT_MAX_ENTRIES;
    localStorage.setItem(AUDIT_STORAGE_KEY, JSON.stringify(next));
  } catch (error) {
    // no-op
  }
};

const buildAuditEntry = (path, options, response) => {
  const stored = readAuthoritativeSessionUser();
  const role = resolveAuthoritativeSessionRole();
  return {
    id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    at: new Date().toISOString(),
    method: String(options?.method || "GET").toUpperCase(),
    path: String(path || ""),
    status: response?.status,
    schoolCode: resolveSchoolCodeHeader(),
    schoolId: resolveSchoolIdHeader(),
    actorId: stored?._id || stored?.id || stored?.userId || "",
    actorName: stored?.name || stored?.email || stored?.ID || "",
    role,
  };
};

const normalizeCacheKey = (value) => String(value || "").trim();
const toCacheTtlMs = (value) => {
  const ttl = Number(value || 0);
  if (!Number.isFinite(ttl) || ttl <= 0) return 0;
  return Math.min(Math.max(1000, Math.floor(ttl)), 10 * 60 * 1000);
};
const responseFromCacheEntry = (entry) => {
  if (!entry) return null;
  const headers = new Headers(
    Array.isArray(entry.headers) ? entry.headers : [],
  );
  return new Response(entry.bodyText || "", {
    status: entry.status || 200,
    headers,
  });
};
const pruneResponseCache = (maxEntries = 200) => {
  if (responseCache.size <= maxEntries) return;
  const entries = Array.from(responseCache.entries());
  entries.sort(
    (a, b) => Number(a?.[1]?.expiresAt || 0) - Number(b?.[1]?.expiresAt || 0),
  );
  const removeCount = Math.max(0, responseCache.size - maxEntries);
  for (let index = 0; index < removeCount; index += 1) {
    const key = entries[index]?.[0];
    if (key) responseCache.delete(key);
  }
};

const erpFetch = async (path, options = {}) => {
  assertFinanceAccess(path, options);
  assertParentRestrictions(path, options);
  const method = String(options?.method || "GET").toUpperCase();
  const requestStartedAt = Date.now();
  const diagnosticPathname =
    typeof window === "undefined" ? "" : window.location?.pathname || "";
  const diagnosticRole = resolveRoleCategory(diagnosticPathname);
  const resolvedOperation = resolveSafeOperation(path, method);
  const diagnosticContext = {
    routeName: resolveSafeRouteName(diagnosticPathname),
    roleCategory: diagnosticRole,
    operation:
      diagnosticRole === "parent" || resolvedOperation === "authentication"
        ? resolvedOperation
        : resolveSafeOperation("", method),
  };
  const recordRequestDiagnostic = (event, payload = {}) =>
    recordClientDiagnostic(event, {
      ...diagnosticContext,
      durationMs: Date.now() - requestStartedAt,
      ...payload,
    });
  const isParentRequest = diagnosticContext.roleCategory === "parent";
  const token = getToken();
  if (!token) {
    if (!isAuthReady()) {
      if (isParentRequest) recordRequestDiagnostic("scope_not_ready");
      throw new Error("Preparing session. Please retry.");
    }
    clearSessionAndRedirectToLogin(SESSION_TIMEOUT_MESSAGE);
    recordRequestDiagnostic("session_expired", {
      operation: "authentication",
      authOutcome: "expired",
    });
    throw new Error(SESSION_TIMEOUT_MESSAGE);
  }

  const portalMode = readActivePortalMode();
  const requestKey =
    method === "GET"
      ? `${token}:${portalMode}:${method}:${buildApiUrl(path, base)}:${String(options?.body || "")}`
      : "";
  const suppressGlobalError = Boolean(options?.suppressGlobalError);
  const cacheTtlMs = requestKey ? toCacheTtlMs(options?.cacheTtlMs) : 0;
  const explicitCacheKey = normalizeCacheKey(options?.cacheKey);
  const responseCacheKey =
    requestKey && cacheTtlMs
      ? `${token}:${explicitCacheKey || requestKey}`
      : "";

  if (responseCacheKey) {
    const cached = responseCache.get(responseCacheKey);
    if (cached && Number(cached.expiresAt || 0) > Date.now()) {
      return responseFromCacheEntry(cached);
    }
    if (cached) {
      responseCache.delete(responseCacheKey);
    }
  }

  if (requestKey && inflightRequests.has(requestKey)) {
    return inflightRequests
      .get(requestKey)
      .then((response) => response.clone());
  }

  const timeoutMs = Number(options?.timeoutMs || 0);
  const requestController = new AbortController();
  authenticatedRequestControllers.add(requestController);
  const fetchOptions = withAuth({
    ...options,
    signal: mergeAbortSignals(options?.signal, requestController.signal),
  });
  delete fetchOptions.timeoutMs;
  delete fetchOptions.cacheTtlMs;
  delete fetchOptions.cacheKey;
  const requestPromise = fetchWithTimeout(
    buildApiUrl(path, base),
    fetchOptions,
    timeoutMs,
  )
    .catch((error) => {
      if (isParentRequest) {
        const offline =
          typeof navigator !== "undefined" && navigator.onLine === false;
        const superseded =
          error?.name === "AbortError" && Boolean(options?.signal?.aborted);
        recordRequestDiagnostic(
          offline
            ? "offline_waiting"
            : superseded
              ? "request_superseded"
              : "request_failed",
          {
            online: !offline,
            superseded,
            failureCategory: offline
              ? "network_unavailable"
              : error?.name === "AbortError"
                ? "timeout"
                : "network_unavailable",
          },
        );
      }
      if (typeof window !== "undefined") {
        try {
          window.dispatchEvent(
            new CustomEvent("erp-network-error", {
              detail: {
                path: String(path || ""),
                kind: error?.name === "AbortError" ? "timeout" : "network",
              },
            }),
          );
        } catch (eventError) {
          // Connectivity state is best-effort; the request error still propagates.
        }
      }
      throw error;
    })
    .then(async (response) => {
      if (response.status === 401) {
        clearSessionAndRedirectToLogin(SESSION_TIMEOUT_MESSAGE);
        recordRequestDiagnostic("session_expired", {
          operation: "authentication",
          authOutcome: "expired",
        });
        return new Response(
          JSON.stringify({ message: SESSION_TIMEOUT_MESSAGE }),
          {
            status: 401,
            headers: { "Content-Type": "application/json" },
          },
        );
      }
      const requestMethod = String(fetchOptions?.method || "GET").toUpperCase();
      if (isParentRequest && response.status >= 400) {
        recordRequestDiagnostic("request_failed", {
          failureCategory:
            response.status === 403
              ? "access_denied"
              : response.status === 404
                ? "not_found"
                : response.status >= 500
                  ? "server_unavailable"
                  : "unknown_failure",
        });
      }
      const isBackgroundTeacherRead =
        response.status === 403 &&
        (requestMethod === "GET" || requestMethod === "HEAD") &&
        resolveAuthoritativeSessionRole() === "teacher";
      if (!suppressGlobalError && !isBackgroundTeacherRead) {
        if (response.status === 403 && isAuthReady()) {
          if (!isParentRequest)
            recordRequestDiagnostic("request_failed", {
              failureCategory: "access_denied",
            });
          pushApiNotice({
            message: "This action is not available for your account.",
            actionLabel: "Go back",
            action: "back",
          });
        } else if (response.status === 404) {
          if (!isParentRequest)
            recordRequestDiagnostic("request_failed", {
              failureCategory: "not_found",
            });
          pushApiNotice({
            message: "We couldn't find that data. Please retry.",
            actionLabel: "Retry",
            action: "reload",
          });
        } else if (response.status >= 500) {
          pushApiNotice({
            message: "The server ran into an issue. Please retry.",
            actionLabel: "Retry",
            action: "reload",
          });
          if (!isParentRequest)
            recordRequestDiagnostic("request_failed", {
              failureCategory: "server_unavailable",
            });
        }
      }
      if (response.ok && shouldAudit(path, options)) {
        recordAuditEvent(buildAuditEntry(path, options, response));
      }
      if (responseCacheKey && response.ok) {
        try {
          const bodyText = await response.clone().text();
          responseCache.set(responseCacheKey, {
            status: response.status,
            headers: Array.from(response.headers.entries()),
            bodyText,
            expiresAt: Date.now() + cacheTtlMs,
          });
          pruneResponseCache();
        } catch (error) {
          // ignore cache write failures
        }
      }
      return response;
    })
    .finally(() => authenticatedRequestControllers.delete(requestController));

  if (requestKey) {
    inflightRequests.set(requestKey, requestPromise);
    requestPromise.then(
      () => inflightRequests.delete(requestKey),
      () => inflightRequests.delete(requestKey),
    );
    return requestPromise.then((response) => response.clone());
  }

  return requestPromise;
};

export const logoutCurrentSession = async () => {
  const token = getToken();
  if (!token) return { loggedOut: true };
  const response = await erpFetch("/api/auth/logout", {
    method: "POST",
    cache: "no-store",
    suppressGlobalError: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
  if (!response.ok) {
    const error = new Error("Unable to end the server session. Please retry.");
    error.status = response.status;
    throw error;
  }
  resetErpAuthenticatedRequestState();
  return parseJsonSafe(response);
};

const publicFetch = (path, options = {}) =>
  fetch(buildApiUrl(path, base), options);
const mergeAbortSignals = (...signals) => {
  const activeSignals = signals.filter(Boolean);
  if (!activeSignals.length) return null;
  if (activeSignals.length === 1) return activeSignals[0];

  const controller = new AbortController();
  const abort = () => {
    if (!controller.signal.aborted) {
      controller.abort();
    }
  };

  activeSignals.forEach((signal) => {
    if (signal.aborted) {
      abort();
      return;
    }
    signal.addEventListener("abort", abort, { once: true });
  });

  return controller.signal;
};

const fetchWithTimeout = async (url, options = {}, timeoutMs = 0) => {
  if (!timeoutMs || typeof AbortController === "undefined") {
    return fetch(url, options);
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  const signal = mergeAbortSignals(options?.signal, controller.signal);

  try {
    return await fetch(url, { ...options, signal });
  } finally {
    clearTimeout(timeoutId);
  }
};

const parseJsonSafe = async (response) => {
  const raw = await response.text();
  const requestId = response?.headers?.get?.("x-request-id") || "";
  const withRequestId = (payload = {}) => {
    if (
      !requestId ||
      !payload ||
      typeof payload !== "object" ||
      Array.isArray(payload)
    )
      return payload;
    return { ...payload, requestId };
  };
  if (!raw) return withRequestId({});
  const contentType = String(
    response?.headers?.get?.("content-type") || "",
  ).toLowerCase();
  const looksLikeHtml =
    /<!doctype|<html|<head|<body/i.test(raw) || /cannot\s+get\s+\/?/i.test(raw);
  if (!contentType.includes("application/json") && looksLikeHtml) {
    const text = raw
      .replace(/<script[\s\S]*?<\/script>/gi, "")
      .replace(/<style[\s\S]*?<\/style>/gi, "")
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 180);
    const status = response?.status ? ` (${response.status})` : "";
    return withRequestId({
      message: `Unexpected server response${status}. Please retry.`,
      details: text || "Server returned HTML instead of JSON.",
      status: response?.status,
      url: response?.url || "",
    });
  }
  try {
    return withRequestId(JSON.parse(raw));
  } catch {
    return withRequestId({
      message: raw,
      status: response?.status,
      url: response?.url || "",
    });
  }
};

const resolveOptionalStatuses = (options = {}) => {
  if (
    Array.isArray(options?.optionalStatuses) &&
    options.optionalStatuses.length
  ) {
    return options.optionalStatuses;
  }
  if (Array.isArray(options?.allowStatuses) && options.allowStatuses.length) {
    return options.allowStatuses;
  }
  if (options?.allowNotFound) return [403, 404];
  return [];
};

const isOptionalStatus = (response, options = {}) =>
  resolveOptionalStatuses(options).includes(response?.status);

const buildStatusError = (response, payload, fallbackMessage) => {
  const error = new Error(
    payload?.message || payload?.error || fallbackMessage,
  );
  error.status = response?.status;
  error.payload = payload;
  return error;
};

const buildQuery = (params = {}) => {
  const query = new URLSearchParams();
  const hasSections = resolveHasSectionsFlag();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    if (value === "undefined" || value === "null") return;
    if (!hasSections && (key === "section" || key === "sectionName")) return;
    if (Array.isArray(value)) {
      value.forEach((item) => query.append(key, item));
      return;
    }
    query.append(key, value);
  });
  const qs = query.toString();
  return qs ? `?${qs}` : "";
};

export const fetchCoursePlan = async (
  { adminId = "", classId = "", sessionId = "" } = {},
  options = {},
) => {
  const res = await erpFetch(
    `/course-plan${buildQuery({ adminId, classId, sessionId })}`,
    {
      method: "GET",
      suppressGlobalError: options?.suppressGlobalError,
      allowNotFound: options?.allowNotFound,
    },
  );
  const payload = await parseJsonSafe(res);
  if (!res.ok && !isOptionalStatus(res, options)) {
    throw buildStatusError(res, payload, "Unable to load course plan.");
  }
  if (options?.returnEnvelope) return payload || null;
  return payload?.plan || null;
};

export const fetchAcademicCurrentWeek = async (adminId = "", options = {}) => {
  const resolvedAdminId =
    typeof adminId === "object"
      ? adminId?.adminId || adminId?.schoolId || ""
      : adminId;
  const res = await erpFetch(
    `/api/academic/current-week${buildQuery({ adminId: resolvedAdminId })}`,
    {
      method: "GET",
      suppressGlobalError: options?.suppressGlobalError,
      allowNotFound: options?.allowNotFound,
      cacheTtlMs: options?.cacheTtlMs === 0 ? 0 : options?.cacheTtlMs || 60000,
    },
  );
  const payload = await parseJsonSafe(res);
  if (!res.ok && !isOptionalStatus(res, options)) {
    throw buildStatusError(res, payload, "Unable to load academic week.");
  }
  return payload || null;
};

export const saveCoursePlanRemote = async (
  {
    adminId = "",
    classId = "",
    sessionId = "",
    payload = null,
    expectedRevision = "",
  } = {},
  options = {},
) => {
  const res = await erpFetch("/course-plan", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      adminId,
      classId,
      sessionId,
      payload,
      expectedRevision,
    }),
    suppressGlobalError: options?.suppressGlobalError,
  });
  const data = await parseJsonSafe(res);
  if (!res.ok) {
    throw buildStatusError(res, data, "Unable to save course plan.");
  }
  return options?.returnEnvelope ? data : data?.plan || null;
};

export const promoteStudents = async (
  { adminId = "", membershipCommands = [], dryRun = true } = {},
  options = {},
) => {
  const res = await erpFetch(`/api/students/promote`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      adminId,
      membershipCommands,
      dryRun,
    }),
    suppressGlobalError: options?.suppressGlobalError,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || ans?.detail || "Failed to promote students",
    );
  }
  return ans;
};

export const submitAdmissionApplication = async (payload) => {
  const res = await publicFetch(`/admissions/guest`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  const ans = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail =
      Array.isArray(ans.fields) && ans.fields.length
        ? `: ${ans.fields.join(", ")}`
        : "";
    throw new Error(`${ans.message || "Failed to submit admission"}${detail}`);
  }
  return ans;
};

export const fetchAdmissions = async (params = {}) => {
  const res = await erpFetch(`/admissions${buildQuery(params)}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
  const ans = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(ans.message || "Failed to fetch admissions");
  }
  return ans;
};

export const fetchAdmission = async (id) => {
  const res = await erpFetch(`/admissions/${id}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
  const ans = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(ans.message || "Failed to fetch admission");
  }
  return ans;
};

export const approveAdmission = async (id, student, review_notes) => {
  const res = await erpFetch(`/admissions/${id}/approve`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ student, review_notes }),
  });
  const ans = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail =
      Array.isArray(ans.fields) && ans.fields.length
        ? `: ${ans.fields.join(", ")}`
        : "";
    throw new Error(`${ans.message || "Failed to approve admission"}${detail}`);
  }
  return ans;
};

export const rejectAdmission = async (id, reason) => {
  const res = await erpFetch(`/admissions/${id}/reject`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ reason }),
  });
  const ans = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(ans.message || "Failed to reject admission");
  }
  return ans;
};

export const fetchPrincipalPulse = async (adminId) => {
  const res = await erpFetch(`/principal/pulse/${adminId}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (!res.ok) {
    throw new Error("Failed to fetch principal pulse");
  }
  const ans = await res.json();
  return ans;
};

export const fetchPrincipalDailySummary = async (params = {}, options = {}) => {
  const res = await erpFetch(
    `/api/dashboard/principal/daily-summary${buildQuery(params)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      suppressGlobalError: options?.suppressGlobalError,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    if (isOptionalStatus(res, options)) return null;
    throw buildStatusError(res, ans, "Failed to fetch principal daily summary");
  }
  return ans;
};

export const fetchReceptionDailySummary = async (params = {}, options = {}) => {
  const res = await erpFetch(
    `/api/dashboard/reception/daily-summary${buildQuery(params)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      suppressGlobalError: options?.suppressGlobalError,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    if (isOptionalStatus(res, options)) return null;
    throw buildStatusError(res, ans, "Failed to fetch reception daily summary");
  }
  return ans;
};

export const fetchPrincipalAbsenteeFollowups = async (
  params = {},
  options = {},
) => {
  const res = await erpFetch(
    `/api/dashboard/principal/absentee-followups${buildQuery(params)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      suppressGlobalError: options?.suppressGlobalError,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    if (isOptionalStatus(res, options)) return null;
    throw buildStatusError(
      res,
      ans,
      "Failed to fetch principal absentee follow-ups",
    );
  }
  return ans;
};

export const createPrincipalAbsenteeFollowup = async (
  payload = {},
  options = {},
) => {
  const res = await erpFetch(`/api/dashboard/principal/absentee-followups`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload || {}),
    suppressGlobalError: options?.suppressGlobalError,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw buildStatusError(res, ans, "Failed to save absentee follow-up");
  }
  return ans;
};

export const fetchFeeAlerts = async (adminId, params = {}, options = {}) => {
  const res = await erpFetch(
    `/api/fees/alerts/${adminId}${buildQuery(params)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      suppressGlobalError: options?.suppressGlobalError,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    if (isOptionalStatus(res, options)) return null;
    throw buildStatusError(res, ans, "Failed to fetch fee alerts");
  }
  return ans;
};

export const fetchCalendarEvents = async (
  adminId,
  params = {},
  options = {},
) => {
  const res = await erpFetch(`/calendar/${adminId}${buildQuery(params)}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    suppressGlobalError: options?.suppressGlobalError,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    if (isOptionalStatus(res, options)) {
      if (isSeedEnabled()) {
        return seedCalendarEvents(params);
      }
      return [];
    }
    throw buildStatusError(res, ans, "Failed to fetch calendar events");
  }
  if (isSeedEnabled()) {
    const list = Array.isArray(ans)
      ? ans
      : Array.isArray(ans?.events)
        ? ans.events
        : Array.isArray(ans?.data)
          ? ans.data
          : Array.isArray(ans?.items)
            ? ans.items
            : [];
    if (!list.length) {
      return seedCalendarEvents(params);
    }
  }
  return ans;
};

export const fetchParentComingUp = async (adminId, options = {}) => {
  const res = await erpFetch(`/calendar/${adminId}/parent-coming-up`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    suppressGlobalError: options?.suppressGlobalError,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw buildStatusError(res, ans, "Failed to fetch upcoming events");
  }
  return ans;
};

export const createCalendarEvent = async (adminId, payload) => {
  const res = await erpFetch(`/calendar/${adminId}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error("Failed to create calendar event");
  }
  const ans = await res.json();
  return ans;
};

export const createCalendarEventsBulk = async (adminId, events = []) => {
  const res = await erpFetch(`/calendar/${adminId}/bulk`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ events }),
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to create calendar events",
    );
  }
  return ans;
};

export const updateCalendarEvent = async (adminId, eventId, payload) => {
  const res = await erpFetch(`/calendar/${adminId}/${eventId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error("Failed to update calendar event");
  }
  const ans = await res.json();
  return ans;
};

export const deleteCalendarEvent = async (adminId, eventId) => {
  const res = await erpFetch(`/calendar/${adminId}/${eventId}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (!res.ok) {
    throw new Error("Failed to delete calendar event");
  }
  const ans = await res.json();
  return ans;
};

export const updateApprovalStatus = async (type, id, action, payload = {}) => {
  const paths = ["/api/approvals", "/approvals"];
  const tokenType = encodeURIComponent(String(type || "").trim());
  const tokenId = encodeURIComponent(String(id || "").trim());
  const tokenAction = encodeURIComponent(String(action || "").trim());

  let lastError = null;
  for (const base of paths) {
    try {
      const res = await erpFetch(
        `${base}/${tokenType}/${tokenId}/${tokenAction}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );
      const ans = await parseJsonSafe(res);
      if (res.ok) return ans;
      if ([404, 405, 501].includes(res.status)) {
        lastError = buildStatusError(res, ans, "Failed to update approval");
        continue;
      }
      throw buildStatusError(res, ans, "Failed to update approval");
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error("Failed to update approval");
};

export const fetchComplaints = async (params = {}) => {
  const res = await erpFetch(`/complaints${buildQuery(params)}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (!res.ok) {
    throw new Error("Failed to fetch complaints");
  }
  const ans = await res.json();
  return ans;
};

export const createComplaint = async (payload) => {
  const res = await erpFetch(`/complaints`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error("Failed to create complaint");
  }
  const ans = await res.json();
  return ans;
};

export const updateComplaint = async (id, payload) => {
  const res = await erpFetch(`/complaints/${id}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error("Failed to update complaint");
  }
  const ans = await res.json();
  return ans;
};

export const createLeaveRequest = async (payload) => {
  const res = await erpFetch(`/leave/requests`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error("Failed to create leave request");
  }
  const ans = await res.json();
  return ans;
};

export const fetchLeaveRequests = async (params = {}) => {
  const res = await erpFetch(`/leave/requests${buildQuery(params)}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (!res.ok) {
    throw new Error("Failed to fetch leave requests");
  }
  const ans = await res.json();
  return ans;
};

export const createInventoryRequest = async (payload) => {
  const res = await erpFetch(`/inventory/requests`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error("Failed to create inventory request");
  }
  const ans = await res.json();
  return ans;
};

export const fetchTeacherMessages = async (params = {}) => {
  const res = await erpFetch(`/teacher/messages${buildQuery(params)}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to fetch teacher messages",
    );
  }
  return ans;
};

export const fetchTeacherStudents = async (params = {}) => {
  const res = await erpFetch(`/api/teacher/students${buildQuery(params)}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    suppressGlobalError: true,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to fetch teacher students",
    );
  }
  return ans;
};

const fetchTeacherStudentResource = async (
  studentId,
  resource = "",
  params = {},
  options = {},
) => {
  const id = String(studentId || "").trim();
  if (!id) throw new Error("studentId is required");
  const suffix = resource ? `/${resource}` : "";
  const response = await erpFetch(
    `/api/teacher/students/${encodeURIComponent(id)}${suffix}${buildQuery(params)}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      suppressGlobalError: true,
      signal: options.signal,
    },
  );
  const payload = await parseJsonSafe(response);
  if (!response.ok) {
    throw buildStatusError(
      response,
      payload,
      payload?.message || "Unable to load the student workspace.",
    );
  }
  return payload;
};

export const fetchTeacherStudentWorkspace = (studentId, options = {}) =>
  fetchTeacherStudentResource(studentId, "", {}, options);

export const fetchTeacherStudentHomework = (
  studentId,
  params = {},
  options = {},
) => fetchTeacherStudentResource(studentId, "homework", params, options);

export const fetchTeacherStudentResults = (studentId, options = {}) =>
  fetchTeacherStudentResource(studentId, "results", {}, options);

export const sendTeacherMessageReply = async (payload = {}) => {
  const res = await erpFetch(`/teacher/messages/reply`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to send teacher reply",
    );
  }
  return ans;
};

export const fetchTeacherWeeklyPlan = async (params = {}) => {
  const res = await erpFetch(`/api/teacher/weekly-plan${buildQuery(params)}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to fetch teacher weekly plan",
    );
  }
  return ans;
};

export const fetchTeacherCalendar = async (params = {}) => {
  const res = await erpFetch(`/api/teacher/calendar${buildQuery(params)}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to fetch the school calendar",
    );
  }
  return ans;
};

export const fetchInventoryRequests = async (params = {}) => {
  const res = await erpFetch(`/inventory/requests${buildQuery(params)}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (!res.ok) {
    throw new Error("Failed to fetch inventory requests");
  }
  const ans = await res.json();
  return ans;
};

export const saveAttendanceRecords = async (adminId, records, options = {}) => {
  const res = await erpFetch(`/api/attendance/${adminId}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ records, ...(options || {}) }),
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to save attendance records",
    );
  }
  return ans;
};

const teacherAttendanceV2Request = async (path, options = {}) => {
  const response = await erpFetch(`/api/teacher/attendance/v2${path}`, {
    method: options.method || "GET",
    headers: { "Content-Type": "application/json" },
    ...(options.body === undefined
      ? {}
      : { body: JSON.stringify(options.body) }),
    suppressGlobalError: true,
    timeoutMs: options.timeoutMs || 15000,
    signal: options.signal,
  });
  const payload = await parseJsonSafe(response);
  if (!response.ok) {
    throw buildStatusError(
      response,
      payload,
      payload?.message || "Attendance is temporarily unavailable.",
    );
  }
  return payload;
};

export const fetchTeacherAttendanceContextV2 = async (
  params = {},
  options = {},
) => teacherAttendanceV2Request(`/context${buildQuery(params)}`, options);

export const saveTeacherAttendanceV2 = async (payload = {}, options = {}) =>
  teacherAttendanceV2Request("/submissions", {
    ...options,
    method: "POST",
    body: payload,
  });

export const requestTeacherAttendanceCorrectionV2 = async (
  payload = {},
  options = {},
) =>
  teacherAttendanceV2Request("/corrections", {
    ...options,
    method: "POST",
    body: payload,
  });

export const fetchTeacherAttendanceChildrenV2 = async (
  params = {},
  options = {},
) => teacherAttendanceV2Request(`/children${buildQuery(params)}`, options);

export const fetchTeacherAttendanceChildHistoryV2 = async (
  studentId,
  params = {},
  options = {},
) =>
  teacherAttendanceV2Request(
    `/children/${studentId}/history${buildQuery(params)}`,
    options,
  );

export const fetchTeacherAttendanceFollowupsV2 = async (
  studentId,
  params = {},
  options = {},
) =>
  teacherAttendanceV2Request(
    `/followups/${studentId}${buildQuery(params)}`,
    options,
  );

export const saveTeacherAttendanceFollowupV2 = async (
  studentId,
  payload = {},
  options = {},
) =>
  teacherAttendanceV2Request(`/followups/${studentId}`, {
    ...options,
    method: "POST",
    body: payload,
  });

export const fetchPrincipalAttendanceCorrectionsV2 = async (params = {}) => {
  const response = await erpFetch(
    `/api/management/attendance/v2/corrections${buildQuery(params)}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      suppressGlobalError: true,
    },
  );
  const payload = await parseJsonSafe(response);
  if (!response.ok)
    throw buildStatusError(
      response,
      payload,
      payload?.message || "Attendance corrections could not be loaded.",
    );
  return payload;
};

export const reviewPrincipalAttendanceCorrectionV2 = async (
  correctionId,
  payload = {},
) => {
  const response = await erpFetch(
    `/api/management/attendance/v2/corrections/${correctionId}/review`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      suppressGlobalError: true,
    },
  );
  const result = await parseJsonSafe(response);
  if (!response.ok)
    throw buildStatusError(
      response,
      result,
      result?.message || "The correction review could not be saved.",
    );
  return result;
};

export const fetchAttendanceSummary = async (
  adminId,
  params = {},
  options = {},
) => {
  const attemptFetch = async (queryParams = {}) => {
    const response = await erpFetch(
      `/api/attendance/${adminId}/summary${buildQuery(queryParams)}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        suppressGlobalError: options?.suppressGlobalError,
      },
    );
    const payload = await parseJsonSafe(response);
    return { response, payload };
  };

  const stripSectionParams = (queryParams = {}) => {
    const next = { ...(queryParams || {}) };
    delete next.section;
    delete next.sectionName;
    delete next.sectionId;
    return next;
  };

  const isSectionColumnError = (payload) =>
    /unknown column ['"]?section['"]?/i.test(
      String(payload?.message || payload?.error || ""),
    );

  const { response, payload } = await attemptFetch(params);
  if (!response.ok) {
    if (isOptionalStatus(response, options)) return null;
    if (isSectionColumnError(payload)) {
      const { response: retryResponse, payload: retryPayload } =
        await attemptFetch(stripSectionParams(params));
      if (retryResponse.ok) return retryPayload;
      if (isOptionalStatus(retryResponse, options)) return null;
      throw buildStatusError(
        retryResponse,
        retryPayload,
        "Failed to fetch attendance summary",
      );
    }
    throw buildStatusError(
      response,
      payload,
      "Failed to fetch attendance summary",
    );
  }
  return payload;
};

export const fetchStudentAttendanceSummary = async (
  adminId,
  params = {},
  options = {},
) => {
  const response = await erpFetch(
    `/api/attendance/${adminId}/student-summary${buildQuery(params)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      suppressGlobalError: options?.suppressGlobalError,
    },
  );
  const payload = await parseJsonSafe(response);
  if (!response.ok) {
    if (isOptionalStatus(response, options)) {
      return { summary: { warnings: [] }, students: [] };
    }
    throw buildStatusError(
      response,
      payload,
      "Failed to fetch student attendance summary",
    );
  }
  return payload;
};

export const fetchPayrollSettings = async (schoolId, options = {}) => {
  const id = String(schoolId || "").trim();
  if (!id) throw new Error("School ID is missing. Please login again.");
  const res = await erpFetch(
    `/api/payroll/settings${buildQuery({ schoolId: id })}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      suppressGlobalError: options?.suppressGlobalError,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to fetch payroll settings",
    );
  }
  return ans;
};

export const updatePayrollSettings = async (payload = {}) => {
  const res = await erpFetch(`/api/payroll/settings`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to update payroll settings",
    );
  }
  return ans;
};

export const fetchPayrollSalarySetup = async (schoolId, options = {}) => {
  const id = String(schoolId || "").trim();
  const res = await erpFetch(
    `/api/payroll/salary-setup${buildQuery({ schoolId: id })}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      suppressGlobalError: options?.suppressGlobalError,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to fetch salary setup",
    );
  }
  return ans;
};

export const updatePayrollSalarySetup = async (
  teacherProfileId,
  payload = {},
) => {
  const id = encodeURIComponent(String(teacherProfileId || "").trim());
  const res = await erpFetch(`/api/payroll/salary-setup/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to update salary setup",
    );
  }
  return ans;
};

export const fetchPayrollSalaryHistory = async (
  teacherProfileId,
  schoolId,
  options = {},
) => {
  const id = encodeURIComponent(String(teacherProfileId || "").trim());
  const school = String(schoolId || "").trim();
  const res = await erpFetch(
    `/api/payroll/salary-setup/${id}/history${buildQuery({ schoolId: school })}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      suppressGlobalError: options?.suppressGlobalError,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to fetch salary history",
    );
  }
  return ans;
};

export const fetchMonthlyPayrollDraft = async (params = {}, options = {}) => {
  const res = await erpFetch(`/api/payroll/monthly${buildQuery(params)}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    suppressGlobalError: options?.suppressGlobalError,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to fetch monthly payroll draft",
    );
  }
  return ans;
};

export const generateMonthlyPayrollDraft = async (payload = {}) => {
  const res = await erpFetch(`/api/payroll/monthly/generate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to generate monthly payroll draft",
    );
  }
  return ans;
};

export const approveMonthlyPayroll = async (runId, payload = {}) => {
  const id = encodeURIComponent(String(runId || "").trim());
  const res = await erpFetch(`/api/payroll/monthly/${id}/approve`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to approve payroll",
    );
  return ans;
};

export const lockMonthlyPayroll = async (runId, payload = {}) => {
  const id = encodeURIComponent(String(runId || "").trim());
  const res = await erpFetch(`/api/payroll/monthly/${id}/lock`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to lock payroll",
    );
  return ans;
};

export const deleteMonthlyPayrollDraft = async (runId, payload = {}) => {
  const id = encodeURIComponent(String(runId || "").trim());
  const res = await erpFetch(
    `/api/payroll/monthly/${id}${buildQuery({ schoolId: payload.schoolId })}`,
    {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to delete payroll draft",
    );
  return ans;
};

export const markPayrollItemPaid = async (runId, itemId, payload = {}) => {
  const encodedRunId = encodeURIComponent(String(runId || "").trim());
  const encodedItemId = encodeURIComponent(String(itemId || "").trim());
  const res = await erpFetch(
    `/api/payroll/monthly/${encodedRunId}/items/${encodedItemId}/mark-paid`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to mark payroll item paid",
    );
  return ans;
};

export const markPayrollItemHold = async (runId, itemId, payload = {}) => {
  const encodedRunId = encodeURIComponent(String(runId || "").trim());
  const encodedItemId = encodeURIComponent(String(itemId || "").trim());
  const res = await erpFetch(
    `/api/payroll/monthly/${encodedRunId}/items/${encodedItemId}/mark-hold`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to hold payroll item",
    );
  return ans;
};

export const markPayrollItemUnpaid = async (runId, itemId, payload = {}) => {
  const encodedRunId = encodeURIComponent(String(runId || "").trim());
  const encodedItemId = encodeURIComponent(String(itemId || "").trim());
  const res = await erpFetch(
    `/api/payroll/monthly/${encodedRunId}/items/${encodedItemId}/mark-unpaid`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to reverse payroll payment",
    );
  return ans;
};

export const fetchPayrollPayslips = async (params = {}, options = {}) => {
  const res = await erpFetch(`/api/payroll/payslips${buildQuery(params)}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    suppressGlobalError: options?.suppressGlobalError,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to fetch payslips",
    );
  return ans;
};

export const fetchPayrollPayslip = async (
  itemId,
  params = {},
  options = {},
) => {
  const id = encodeURIComponent(String(itemId || "").trim());
  const res = await erpFetch(
    `/api/payroll/payslips/${id}${buildQuery(params)}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      suppressGlobalError: options?.suppressGlobalError,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to fetch payslip",
    );
  return ans;
};

export const fetchTeacherSalaryStatement = async (
  params = {},
  options = {},
) => {
  const res = await erpFetch(
    `/api/teacher/payroll/salary-statement${buildQuery(params)}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      suppressGlobalError: options?.suppressGlobalError,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to fetch salary statement",
    );
  return ans;
};

export const fetchTeacherPayrollPayslip = async (params = {}, options = {}) => {
  const res = await erpFetch(
    `/api/teacher/payroll/payslip${buildQuery(params)}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      suppressGlobalError: options?.suppressGlobalError,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to fetch payslip",
    );
  return ans;
};

export const fetchTeacherExpenseClaims = async (params = {}, options = {}) => {
  const res = await erpFetch(
    `/api/teacher/payroll/expense-claims${buildQuery(params)}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      suppressGlobalError: options?.suppressGlobalError,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to fetch expense requests",
    );
  return ans;
};

export const fetchTeacherPayrollAttendance = async (
  params = {},
  options = {},
) => {
  const res = await erpFetch(
    `/api/teacher/payroll/my-attendance${buildQuery(params)}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      suppressGlobalError: options?.suppressGlobalError,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to fetch payroll attendance",
    );
  return ans;
};

export const fetchTeacherStaffAttendanceToday = async (
  params = {},
  options = {},
) => {
  const res = await erpFetch(
    `/api/teacher/staff-attendance/today${buildQuery(params)}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      suppressGlobalError: options?.suppressGlobalError,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to fetch today's staff attendance",
    );
  return ans;
};

export const submitTeacherStaffAttendanceReview = async (payload = {}) => {
  const res = await erpFetch(`/api/teacher/staff-attendance/today/review`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to submit attendance review",
    );
  return ans;
};

export const fetchStaffAttendanceReviewRequests = async (
  params = {},
  options = {},
) => {
  const res = await erpFetch(
    `/api/staff-attendance/review-requests${buildQuery(params)}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      suppressGlobalError: options?.suppressGlobalError,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message ||
        ans?.error ||
        "Failed to fetch attendance review requests",
    );
  return ans;
};

export const reviewStaffAttendanceRequest = async (requestId, payload = {}) => {
  const id = encodeURIComponent(String(requestId || "").trim());
  const res = await erpFetch(`/api/staff-attendance/review-requests/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to review attendance request",
    );
  return ans;
};

export const submitTeacherExpenseClaim = async (payload = {}) => {
  const res = await erpFetch(`/api/teacher/payroll/expense-claims`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to submit expense request",
    );
  return ans;
};

export const fetchTeacherExams = async (params = {}, options = {}) => {
  const res = await erpFetch(`/api/teacher/exams${buildQuery(params)}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    suppressGlobalError: options?.suppressGlobalError,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to fetch teacher exams",
    );
  return ans;
};

export const fetchTeacherExamSubjects = async (examId, options = {}) => {
  const id = encodeURIComponent(String(examId || "").trim());
  const res = await erpFetch(`/api/teacher/exams/${id}/subjects`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    suppressGlobalError: options?.suppressGlobalError,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to fetch exam subjects",
    );
  return ans;
};

export const fetchTeacherExamMarksEntry = async (
  examId,
  params = {},
  options = {},
) => {
  const id = encodeURIComponent(String(examId || "").trim());
  const res = await erpFetch(
    `/api/teacher/exams/${id}/marks-entry${buildQuery(params)}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      suppressGlobalError: options?.suppressGlobalError,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to fetch marks entry",
    );
  return ans;
};

export const saveTeacherExamMarksEntry = async (examId, payload = {}) => {
  const id = encodeURIComponent(String(examId || "").trim());
  const res = await erpFetch(`/api/teacher/exams/${id}/marks-entry`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to save marks",
    );
  return ans;
};

export const submitTeacherExamSubject = async (examId, payload = {}) => {
  const id = encodeURIComponent(String(examId || "").trim());
  const res = await erpFetch(`/api/teacher/exams/${id}/submit-subject`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to submit subject marks",
    );
  return ans;
};

export const fetchTeacherExamClassReview = async (
  examId,
  params = {},
  options = {},
) => {
  const id = encodeURIComponent(String(examId || "").trim());
  const res = await erpFetch(
    `/api/teacher/exams/${id}/class-review${buildQuery(params)}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      suppressGlobalError: options?.suppressGlobalError,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to fetch class review",
    );
  return ans;
};

export const createTeacherExamFlag = async (examId, payload = {}) => {
  const id = encodeURIComponent(String(examId || "").trim());
  const res = await erpFetch(`/api/teacher/exams/${id}/flags`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to flag result issue",
    );
  return ans;
};

export const fetchPayrollAdjustments = async (params = {}, options = {}) => {
  const res = await erpFetch(`/api/payroll/adjustments${buildQuery(params)}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    suppressGlobalError: options?.suppressGlobalError,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to fetch payroll adjustments",
    );
  return ans;
};

export const createPayrollAdjustment = async (payload = {}) => {
  const res = await erpFetch(`/api/payroll/adjustments`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to create payroll adjustment",
    );
  return ans;
};

export const cancelPayrollAdjustment = async (adjustmentId, payload = {}) => {
  const id = encodeURIComponent(String(adjustmentId || "").trim());
  const res = await erpFetch(`/api/payroll/adjustments/${id}/cancel`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to cancel payroll adjustment",
    );
  return ans;
};

export const fetchPayrollExpenseClaims = async (params = {}, options = {}) => {
  const res = await erpFetch(
    `/api/payroll/expense-claims${buildQuery(params)}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      suppressGlobalError: options?.suppressGlobalError,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to fetch expense claims",
    );
  return ans;
};

export const reviewPayrollExpenseClaim = async (claimId, payload = {}) => {
  const id = encodeURIComponent(String(claimId || "").trim());
  const res = await erpFetch(`/api/payroll/expense-claims/${id}/review`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to review expense claim",
    );
  return ans;
};

export const fetchStaffAttendance = async (params = {}, options = {}) => {
  const res = await erpFetch(
    `/api/payroll/staff-attendance${buildQuery(params)}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      suppressGlobalError: options?.suppressGlobalError,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to fetch staff attendance",
    );
  }
  return ans;
};

export const saveStaffAttendance = async (payload = {}) => {
  const res = await erpFetch(`/api/payroll/staff-attendance/mark`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to save staff attendance",
    );
  }
  return ans;
};

export const fetchStaffAttendanceSummary = async (
  params = {},
  options = {},
) => {
  const res = await erpFetch(
    `/api/payroll/staff-attendance/summary${buildQuery(params)}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      suppressGlobalError: options?.suppressGlobalError,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to fetch staff attendance summary",
    );
  }
  return ans;
};

export const fetchStaffAttendanceDashboardSummary = async (
  params = {},
  options = {},
) => {
  const res = await erpFetch(
    `/api/staff-attendance/dashboard-summary${buildQuery(params)}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      suppressGlobalError: options?.suppressGlobalError,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw buildStatusError(
      res,
      ans,
      ans?.message ||
        ans?.error ||
        "Failed to fetch staff attendance dashboard summary",
    );
  }
  return ans;
};

export const fetchStaffAttendanceMissing = async (
  params = {},
  options = {},
) => {
  const res = await erpFetch(
    `/api/payroll/staff-attendance/missing${buildQuery(params)}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      suppressGlobalError: options?.suppressGlobalError,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to fetch staff attendance warnings",
    );
  }
  return ans;
};

export const fetchExams = async (params = {}, options = {}) => {
  const res = await erpFetch(`/api/exams${buildQuery(params)}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    suppressGlobalError: options?.suppressGlobalError,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to fetch exams",
    );
  return ans;
};

export const createExam = async (payload = {}) => {
  const res = await erpFetch(`/api/exams`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to create exam",
    );
  return ans;
};

export const fetchExam = async (examId, options = {}) => {
  const id = encodeURIComponent(String(examId || "").trim());
  const res = await erpFetch(`/api/exams/${id}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    suppressGlobalError: options?.suppressGlobalError,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to fetch exam",
    );
  return ans;
};

export const updateExam = async (examId, payload = {}) => {
  const id = encodeURIComponent(String(examId || "").trim());
  const res = await erpFetch(`/api/exams/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to update exam",
    );
  return ans;
};

export const openExamEntry = async (examId, payload = {}) => {
  const id = encodeURIComponent(String(examId || "").trim());
  const res = await erpFetch(`/api/exams/${id}/open-entry`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to open exam entry",
    );
  return ans;
};

export const scheduleExam = async (examId, payload = {}) => {
  const id = encodeURIComponent(String(examId || "").trim());
  const res = await erpFetch(`/api/exams/${id}/schedule`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to schedule exam",
    );
  return ans;
};

export const closeExamEntry = async (examId, payload = {}) => {
  const id = encodeURIComponent(String(examId || "").trim());
  const res = await erpFetch(`/api/exams/${id}/close-entry`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to close exam entry",
    );
  return ans;
};

export const publishExamResults = async (examId, payload = {}) => {
  const id = encodeURIComponent(String(examId || "").trim());
  const res = await erpFetch(`/api/exams/${id}/publish`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to publish results",
    );
  return ans;
};

export const reopenExamSubjectMarks = async (
  examId,
  examSubjectId,
  payload = {},
) => {
  const id = encodeURIComponent(String(examId || "").trim());
  const subjectId = encodeURIComponent(String(examSubjectId || "").trim());
  const res = await erpFetch(`/api/exams/${id}/subjects/${subjectId}/reopen`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to reopen marks",
    );
  return ans;
};

export const fetchExamProgress = async (examId, options = {}) => {
  const id = encodeURIComponent(String(examId || "").trim());
  const res = await erpFetch(`/api/exams/${id}/progress`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    suppressGlobalError: options?.suppressGlobalError,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to fetch exam progress",
    );
  return ans;
};

export const getParentResults = async (options = {}) => {
  const res = await erpFetch(`/api/parent/results`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    suppressGlobalError: options?.suppressGlobalError,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to fetch results",
    );
  return ans;
};

export const getParentExamSchedule = async (options = {}) => {
  const res = await erpFetch(`/api/parent/exam-schedule`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    suppressGlobalError: options?.suppressGlobalError,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to fetch exam schedule",
    );
  return ans;
};

export const getParentResultDetail = async (examId, options = {}) => {
  const id = encodeURIComponent(String(examId || "").trim());
  const res = await erpFetch(`/api/parent/results/${id}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    suppressGlobalError: options?.suppressGlobalError,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to fetch result",
    );
  return ans;
};

export const fetchAttendanceInsights = async (
  adminId,
  params = {},
  options = {},
) => {
  const response = await erpFetch(
    `/api/attendance-insights/${adminId}${buildQuery(params)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      suppressGlobalError: options?.suppressGlobalError,
    },
  );
  const payload = await parseJsonSafe(response);
  if (!response.ok) {
    if (isOptionalStatus(response, options)) return null;
    throw buildStatusError(
      response,
      payload,
      "Failed to fetch attendance insights",
    );
  }
  return payload;
};

// -----------------------------
// Zero Period (Paste-Only) APIs
// -----------------------------

export const fetchZeroPeriodTeacher = async (params = {}) => {
  const res = await erpFetch(`/zero-period/teacher${buildQuery(params)}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to load Zero Period (teacher)",
    );
  }
  return ans;
};

export const fetchZeroPeriodTeacherDefault = async (params = {}) => {
  const res = await erpFetch(
    `/zero-period/teacher/default${buildQuery(params)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to load Zero Period default",
    );
  }
  return ans;
};

export const saveZeroPeriodTeacherRelease = async (payload = {}) => {
  const res = await erpFetch(`/zero-period/teacher/release`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to save Zero Period release",
    );
  }
  return ans;
};

export const fetchZeroPeriodStudent = async (params = {}) => {
  const res = await erpFetch(`/zero-period/student${buildQuery(params)}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to load Zero Period (student)",
    );
  }
  return ans;
};

export const saveZeroPeriodStudentSelfCheck = async ({
  itemId,
  ...payload
} = {}) => {
  const res = await erpFetch(
    `/zero-period/student/self-check/${encodeURIComponent(String(itemId || ""))}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...(payload.clientRequestId
          ? { "Idempotency-Key": payload.clientRequestId }
          : {}),
      },
      body: JSON.stringify(payload),
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw buildStatusError(
      res,
      ans,
      ans?.message || "Daily Test could not be saved right now.",
    );
  }
  return ans;
};

export const fetchZeroPeriodTeacherInsights = async (params = {}) => {
  const res = await erpFetch(
    `/zero-period/teacher/insights${buildQuery(params)}`,
    { method: "GET", headers: { "Content-Type": "application/json" } },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw new Error(ans?.message || "Daily Test insights are unavailable.");
  return ans;
};

export const fetchZeroPeriodTeacherProgress = async (params = {}) => {
  const res = await erpFetch(
    `/zero-period/teacher/progress${buildQuery(params)}`,
    { method: "GET", headers: { "Content-Type": "application/json" } },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw new Error(
      ans?.message || "Zero Period progress is unavailable right now.",
    );
  return ans;
};

export const fetchZeroPeriodPrincipalInsights = async (params = {}) => {
  const res = await erpFetch(
    `/zero-period/principal/insights${buildQuery(params)}`,
    { method: "GET", headers: { "Content-Type": "application/json" } },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw new Error(ans?.message || "Daily Test insights are unavailable.");
  return ans;
};

export const fetchZeroPeriodAdminDays = async (params = {}) => {
  const res = await erpFetch(`/zero-period/admin/days${buildQuery(params)}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to load Zero Period days",
    );
  }
  return ans;
};

export const saveZeroPeriodAdminDay = async (payload = {}) => {
  const res = await erpFetch(`/zero-period/admin/days`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to save Zero Period day",
    );
  }
  return ans;
};

export const saveZeroPeriodAdminConfig = async (payload = {}) => {
  const res = await erpFetch(`/zero-period/admin/config`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to save Zero Period config",
    );
  }
  return ans;
};

export const sendParentChat = async (payload, options = {}) => {
  const res = await erpFetch(`/api/comms/messages`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload || {}),
    suppressGlobalError: options?.suppressGlobalError,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to send Chat message",
    );
  }
  return ans;
};

const shouldSeedCommunication = (params = {}) => {
  if (!isSeedEnabled()) return false;
  const className = String(params?.className || params?.class || "").trim();
  const targetType = String(params?.targetType || "")
    .trim()
    .toLowerCase();
  return Boolean(className || targetType === "class");
};

export const fetchCommunication = async (params = {}, options = {}) => {
  const role = resolveAuthoritativeSessionRole();
  const safeOptions = { ...(options || {}) };
  if (role === "teacher") {
    // Teacher pages use communication reads for badges and page context. A forbidden optional
    // read should not create a scary global access banner while the usable page remains open.
    if (safeOptions.suppressGlobalError === undefined)
      safeOptions.suppressGlobalError = true;
    if (safeOptions.allowNotFound === undefined)
      safeOptions.allowNotFound = true;
  }
  const res = await erpFetch(`/api/communication${buildQuery(params)}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    suppressGlobalError: safeOptions?.suppressGlobalError,
    cacheTtlMs:
      safeOptions?.cacheTtlMs === 0 ? 0 : safeOptions?.cacheTtlMs || 15000,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    if (isOptionalStatus(res, safeOptions)) {
      if (shouldSeedCommunication(params)) {
        return seedCommunicationMessages(params);
      }
      return { messages: [] };
    }
    throw buildStatusError(res, ans, "Failed to fetch communication");
  }
  const messages = Array.isArray(ans?.messages) ? ans.messages : [];
  if (shouldSeedCommunication(params) && !messages.length) {
    return seedCommunicationMessages(params);
  }
  return ans;
};

export const fetchCurrentParentCommunication = async (
  params = {},
  options = {},
) => {
  const res = await erpFetch(
    `/api/parent/communication/current${buildQuery(params)}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      suppressGlobalError: options?.suppressGlobalError,
      cacheTtlMs: options?.cacheTtlMs === 0 ? 0 : options?.cacheTtlMs || 15000,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    if (isOptionalStatus(res, options)) {
      return {
        items: [],
        needsResponse: [],
        fromSchool: [],
        conversations: [],
        unreadCount: 0,
        requiresResponseCount: 0,
        warnings: [],
      };
    }
    throw buildStatusError(
      res,
      ans,
      "Failed to fetch current parent communication",
    );
  }
  return ans;
};

export const fetchHomeworkAssignments = async (params = {}, options = {}) => {
  const res = await erpFetch(`/api/homework/assignments${buildQuery(params)}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    suppressGlobalError: options?.suppressGlobalError,
    cacheTtlMs: options?.cacheTtlMs === 0 ? 0 : options?.cacheTtlMs || 15000,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    if (isOptionalStatus(res, options)) return { assignments: [] };
    throw buildStatusError(res, ans, "Failed to fetch homework assignments");
  }
  return ans;
};

export const createHomeworkAssignment = async (payload = {}, options = {}) => {
  const idempotencyKey = String(
    options?.idempotencyKey || payload?.idempotencyKey || "",
  ).trim();
  const res = await erpFetch(`/api/homework/assignments`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}),
    },
    body: JSON.stringify(payload || {}),
    suppressGlobalError: options?.suppressGlobalError,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw buildStatusError(res, ans, "Failed to create homework assignment");
  }
  return ans;
};

export const updateHomeworkAssignment = async (
  assignmentId,
  payload = {},
  options = {},
) => {
  const token = String(assignmentId || "").trim();
  if (!token) throw new Error("assignmentId is required");
  const res = await erpFetch(
    `/api/homework/assignments/${encodeURIComponent(token)}`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload || {}),
      suppressGlobalError: options?.suppressGlobalError,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw buildStatusError(res, ans, "Failed to update homework assignment");
  }
  return ans;
};

export const fetchBroadcasts = async (params = {}, options = {}) => {
  const res = await erpFetch(`/api/broadcasts${buildQuery(params)}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    suppressGlobalError: options?.suppressGlobalError,
    cacheTtlMs: options?.cacheTtlMs === 0 ? 0 : options?.cacheTtlMs || 5000,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) throw buildStatusError(res, ans, "Failed to load Broadcasts");
  return ans;
};

export const fetchBroadcastAudienceOptions = async (
  params = {},
  options = {},
) => {
  const res = await erpFetch(
    `/api/broadcasts/audience-options${buildQuery(params)}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      suppressGlobalError: options?.suppressGlobalError,
      cacheTtlMs: options?.cacheTtlMs === 0 ? 0 : options?.cacheTtlMs || 15000,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(res, ans, "Failed to load Broadcast audiences");
  return ans;
};

export const previewBroadcastAudience = async (payload = {}, options = {}) => {
  const res = await erpFetch("/api/broadcasts/audience-preview", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload || {}),
    suppressGlobalError: options?.suppressGlobalError,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(res, ans, "Failed to preview Broadcast audience");
  return ans;
};

export const createBroadcast = async (payload = {}, options = {}) => {
  const res = await erpFetch("/api/broadcasts", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload || {}),
    suppressGlobalError: options?.suppressGlobalError,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) throw buildStatusError(res, ans, "Failed to create Broadcast");
  return ans;
};

export const updateBroadcastState = async (id, action, options = {}) => {
  const token = String(id || "").trim();
  if (!token) throw new Error("id is required");
  const res = await erpFetch(
    `/api/broadcasts/${encodeURIComponent(token)}/state`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action }),
      suppressGlobalError: options?.suppressGlobalError,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(res, ans, "Failed to update Broadcast state");
  return ans;
};

export const acknowledgeNotice = async (id, payload = {}, options = {}) => {
  const token = String(id || "").trim();
  if (!token) throw new Error("id is required");
  const res = await erpFetch(
    `/api/notices/${encodeURIComponent(token)}/acknowledge`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload || {}),
      suppressGlobalError: options?.suppressGlobalError,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    if (isOptionalStatus(res, options)) return ans;
    throw buildStatusError(res, ans, "Failed to acknowledge notice");
  }
  return ans;
};

export const exportStudentCommunicationHistory = async (
  studentId,
  params = {},
) => {
  const token = String(studentId || "").trim();
  if (!token) throw new Error("studentId is required");
  const res = await erpFetch(
    `/api/communication/student/${encodeURIComponent(token)}/export${buildQuery(params)}`,
    {
      method: "GET",
      headers: {
        Accept: "text/csv",
      },
    },
  );
  if (!res.ok) {
    const ans = await parseJsonSafe(res);
    throw new Error(
      ans?.message || ans?.error || "Failed to export communication history",
    );
  }
  const blob = await res.blob();
  const disposition = res.headers?.get?.("content-disposition") || "";
  const match = disposition.match(/filename="?([^"]+)"?/i);
  return {
    blob,
    filename: match?.[1] || "",
  };
};

export const exportStudents = async (params = {}) => {
  const res = await erpFetch(`/api/students/export${buildQuery(params)}`, {
    method: "GET",
    headers: {
      Accept: "text/csv",
    },
  });
  if (!res.ok) {
    const ans = await parseJsonSafe(res);
    throw new Error(ans?.message || ans?.error || "Failed to export students");
  }
  const blob = await res.blob();
  const disposition = res.headers?.get?.("content-disposition") || "";
  const match = disposition.match(/filename="?([^";]+)"?/i);
  return {
    blob,
    filename: match?.[1] || "students.csv",
  };
};

export const fetchManagementHomeworkOverview = async (
  params = {},
  options = {},
) => {
  const res = await erpFetch(
    `/api/management/homework/overview${buildQuery(params)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      suppressGlobalError: options?.suppressGlobalError,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    if (isOptionalStatus(res, options)) return null;
    throw new Error(
      ans?.message || ans?.error || "Failed to fetch homework overview",
    );
  }
  return ans;
};

export const fetchManagementCorrectionRequests = async (
  params = {},
  options = {},
) => {
  const res = await erpFetch(
    `/api/management/corrections${buildQuery(params)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      suppressGlobalError: options?.suppressGlobalError,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    if (isOptionalStatus(res, options)) {
      return { requests: [] };
    }
    throw new Error(
      ans?.message || ans?.error || "Failed to load correction requests",
    );
  }
  return ans;
};

export const updateManagementCorrectionRequest = async (
  id,
  payload = {},
  options = {},
) => {
  const token = String(id || "").trim();
  if (!token) throw new Error("id is required");
  const res = await erpFetch(
    `/api/management/corrections/${encodeURIComponent(token)}`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload || {}),
      suppressGlobalError: options?.suppressGlobalError,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    if (isOptionalStatus(res, options)) {
      return ans || {};
    }
    throw new Error(
      ans?.message || ans?.error || "Failed to update correction request",
    );
  }
  return ans;
};

export const fetchCommsMessages = async (params = {}) => {
  const res = await erpFetch(`/api/comms/messages${buildQuery(params)}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(ans?.message || ans?.error || "Failed to fetch messages");
  }
  return ans;
};

export const replyCommsMessage = async (
  messageId,
  payload = {},
  options = {},
) => {
  const id = String(messageId || payload?.id || "").trim();
  if (!id) throw new Error("messageId is required");
  const body = String(
    payload?.body || payload?.message || payload?.reply || "",
  ).trim();
  if (!body) throw new Error("Reply text is required");
  const res = await erpFetch(
    `/api/comms/messages/${encodeURIComponent(id)}/reply`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ body }),
      suppressGlobalError: options?.suppressGlobalError,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(ans?.message || ans?.error || "Failed to send reply");
  }
  return ans;
};

export const setCommsConversationState = async (messageId, state) => {
  const id = String(messageId || "").trim();
  const normalizedState = String(state || "")
    .trim()
    .toUpperCase();
  if (!id) throw new Error("messageId is required");
  if (!["OPEN", "RESOLVED"].includes(normalizedState)) {
    throw new Error("state must be OPEN or RESOLVED");
  }
  const res = await erpFetch(
    `/api/comms/messages/${encodeURIComponent(id)}/state`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ state: normalizedState }),
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to update Chat state",
    );
  }
  return ans;
};

export const escalateCommsMessage = async (messageId, payload = {}) => {
  const id = String(messageId || payload?.id || "").trim();
  if (!id) throw new Error("messageId is required");
  const toRole = String(payload?.toRole || payload?.role || "").trim();
  if (!toRole) throw new Error("toRole is required");
  const res = await erpFetch(
    `/api/comms/messages/${encodeURIComponent(id)}/escalate`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ ...payload, toRole }),
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(ans?.message || ans?.error || "Failed to escalate message");
  }
  return ans;
};

export const markCommunicationMessageRead = async (
  id,
  payload = {},
  options = {},
) => {
  const readPathResponse = await erpFetch(`/api/communication/${id}/read`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
    suppressGlobalError: options?.suppressGlobalError,
  });
  const readPathBody = await parseJsonSafe(readPathResponse);
  if (readPathResponse.ok) {
    return readPathBody;
  }
  if (isOptionalStatus(readPathResponse, options)) return readPathBody;
  throw buildStatusError(
    readPathResponse,
    readPathBody,
    "Failed to mark communication as read",
  );
};

export const markHomeworkComplete = async (payload = {}, options = {}) => {
  assertHomeworkCompletionWriteAccess();
  const body = normalizeHomeworkCompletionPayload(payload);
  const res = await erpFetch(`/homework/mark-complete`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
    suppressGlobalError: options?.suppressGlobalError,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to mark homework complete",
    );
  }
  return ans;
};

export const markHomeworkIncomplete = async (payload = {}, options = {}) => {
  assertHomeworkCompletionWriteAccess();
  const body = normalizeHomeworkCompletionPayload(payload);
  const res = await erpFetch(`/homework/mark-incomplete`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
    suppressGlobalError: options?.suppressGlobalError,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to undo homework completion",
    );
  }
  return ans;
};

export const fetchHomeworkCompletions = async (params = {}, options = {}) => {
  const query = buildQuery(params);
  const res = await erpFetch(`/homework/completions${query}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    suppressGlobalError: options?.suppressGlobalError,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    if (isOptionalStatus(res, options)) {
      return { completions: [], available: false };
    }
    throw buildStatusError(res, ans, "Failed to load homework completions");
  }
  return {
    completions: Array.isArray(ans?.completions) ? ans.completions : [],
    available: true,
  };
};

export const fetchStudent360 = async (
  adminId,
  studentId,
  reason,
  options = {},
) => {
  const query = {
    reason,
    ...(options?.query && typeof options.query === "object"
      ? options.query
      : {}),
  };
  const res = await erpFetch(
    `/api/students/${adminId}/360/${studentId}${buildQuery(query)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      suppressGlobalError: options?.suppressGlobalError,
      cacheTtlMs: options?.cacheTtlMs === 0 ? 0 : options?.cacheTtlMs || 30000,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    if (isOptionalStatus(res, options)) return { attendance: [] };
    throw buildStatusError(res, ans, "Failed to fetch student 360");
  }
  return ans;
};

export const createStudentIncident = async (adminId, payload) => {
  const res = await erpFetch(`/api/students/${adminId}/incidents`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error("Failed to create incident");
  }
  const ans = await res.json();
  return ans;
};

export const createParentContact = async (adminId, payload) => {
  const res = await erpFetch(`/api/students/${adminId}/contacts`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error("Failed to create parent contact");
  }
  const ans = await res.json();
  return ans;
};

export const createIntervention = async (adminId, payload) => {
  const res = await erpFetch(`/api/students/${adminId}/interventions`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error("Failed to create intervention");
  }
  const ans = await res.json();
  return ans;
};

const normalizeParentLoginPayload = (payload = {}) => {
  const rawRole = String(payload?.role || "")
    .trim()
    .toLowerCase();
  if (!["parent", "student"].includes(rawRole)) return payload;

  const token = toSafeString(payload?.token);
  const studentId = resolveStudentIdCandidate(
    payload?.studentId,
    payload?.student_id,
    payload?.activeStudentId,
    payload?._id,
    payload?.id,
    payload?.erpId,
    payload?.erpID,
    payload?.ID,
    payload?.login_id,
    payload?.loginId,
    payload?.username,
    payload?.user?.studentId,
    payload?.user?.student_id,
    payload?.user?.activeStudentId,
    payload?.user?._id,
    payload?.user?.id,
    payload?.user?.erpId,
    payload?.user?.erpID,
    payload?.user?.ID,
    payload?.user?.login_id,
    payload?.user?.loginId,
    payload?.user?.userId,
    payload?.user?.username,
    payload?.user?.email,
  );
  if (!token || !studentId) return payload;

  const academicYear = toSafeString(
    payload?.academicYear ||
      payload?.academic_year ||
      payload?.session ||
      payload?.academic_session ||
      payload?.current_session,
  );

  const normalized = {
    ...payload,
    token,
    role: "STUDENT",
    studentId,
    academicYear,
  };
  if (payload?.user && typeof payload.user === "object") {
    normalized.user = {
      ...payload.user,
      role: "STUDENT",
      studentId,
    };
  }
  return normalized;
};

const extractTokenString = (...candidates) => {
  for (const candidate of candidates) {
    if (candidate === null || candidate === undefined) continue;
    if (typeof candidate === "string") {
      const token = candidate.replace(/^Bearer\s+/i, "").trim();
      if (token) return token;
      continue;
    }
    if (typeof candidate === "object") {
      const nested = extractTokenString(
        candidate?.token,
        candidate?.accessToken,
        candidate?.access_token,
        candidate?.jwt,
        candidate?.idToken,
        candidate?.id_token,
      );
      if (nested) return nested;
    }
  }
  return "";
};

const normalizeLoginEnvelope = (payload = {}) => {
  const source = payload && typeof payload === "object" ? payload : {};
  const nested =
    source?.data && typeof source.data === "object" ? source.data : {};
  const merged = { ...nested, ...source };

  const token = extractTokenString(
    merged?.token,
    merged?.accessToken,
    merged?.access_token,
    merged?.jwt,
    merged?.idToken,
    merged?.id_token,
    source?.token,
    source?.accessToken,
    nested?.token,
    nested?.accessToken,
  );
  if (token) merged.token = token;

  if (!merged.user) {
    merged.user =
      source?.user ||
      nested?.user ||
      source?.admin_data ||
      nested?.admin_data ||
      source?.teacher_data ||
      nested?.teacher_data ||
      null;
  }
  if (!merged.admin_data && nested?.admin_data)
    merged.admin_data = nested.admin_data;
  if (!merged.teacher_data && nested?.teacher_data)
    merged.teacher_data = nested.teacher_data;
  if (!merged.student_data && nested?.student_data)
    merged.student_data = nested.student_data;
  if (!merged.parent_data && nested?.parent_data)
    merged.parent_data = nested.parent_data;

  const resolvedRole = toSafeString(
    merged?.role ||
      merged?.user?.role ||
      merged?.admin_data?.role ||
      merged?.teacher_data?.role ||
      merged?.student_data?.role ||
      merged?.parent_data?.role,
  );
  if (resolvedRole) merged.role = resolvedRole;

  return merged;
};

const normalizeLoginPayload = (payload = {}) => {
  const envelopeNormalized = normalizeLoginEnvelope(payload);
  return normalizeParentLoginPayload(envelopeNormalized);
};

const extractLoginErrorMessage = (payload = {}) => {
  const source = payload && typeof payload === "object" ? payload : {};
  const nested =
    source?.data && typeof source.data === "object" ? source.data : {};
  const candidates = [
    source?.errors?.[0]?.msg,
    source?.errors?.[0]?.message,
    source?.message,
    source?.error,
    source?.msg,
    nested?.errors?.[0]?.msg,
    nested?.errors?.[0]?.message,
    nested?.message,
    nested?.error,
    nested?.msg,
  ];
  for (const candidate of candidates) {
    const value = toSafeString(candidate);
    if (value) return value;
  }
  return "";
};

const normalizeSessionEnvelope = (payload = {}) => {
  const source = payload && typeof payload === "object" ? payload : {};
  const nested =
    source?.data && typeof source.data === "object" ? source.data : {};
  const merged = { ...nested, ...source };

  if (!merged.user && nested?.user && typeof nested.user === "object") {
    merged.user = nested.user;
  }
  if (
    !merged.admin_data &&
    nested?.admin_data &&
    typeof nested.admin_data === "object"
  ) {
    merged.admin_data = nested.admin_data;
  }
  if (
    !merged.teacher_data &&
    nested?.teacher_data &&
    typeof nested.teacher_data === "object"
  ) {
    merged.teacher_data = nested.teacher_data;
  }
  if (
    !merged.student_data &&
    nested?.student_data &&
    typeof nested.student_data === "object"
  ) {
    merged.student_data = nested.student_data;
  }
  if (
    !merged.parent_data &&
    nested?.parent_data &&
    typeof nested.parent_data === "object"
  ) {
    merged.parent_data = nested.parent_data;
  }

  const resolvedRole = toSafeString(
    merged?.role ||
      merged?.user?.role ||
      merged?.admin_data?.role ||
      merged?.teacher_data?.role ||
      merged?.student_data?.role ||
      merged?.parent_data?.role,
  );
  if (resolvedRole) merged.role = resolvedRole;

  const normalizedRole = resolvedRole.toLowerCase();
  if (
    !merged.admin_data &&
    normalizedRole === "admin" &&
    merged?.user &&
    typeof merged.user === "object"
  ) {
    merged.admin_data = merged.user;
  }
  if (
    !merged.teacher_data &&
    normalizedRole === "teacher" &&
    merged?.user &&
    typeof merged.user === "object"
  ) {
    merged.teacher_data = merged.user;
  }
  if (
    !merged.student_data &&
    normalizedRole === "student" &&
    merged?.user &&
    typeof merged.user === "object"
  ) {
    merged.student_data = merged.user;
  }
  if (
    !merged.parent_data &&
    normalizedRole === "parent" &&
    merged?.user &&
    typeof merged.user === "object"
  ) {
    merged.parent_data = merged.user;
  }

  return merged;
};

export const login_user = async (obj) => {
  const requestOptions = {
    method: "POST",
    cache: "no-store",
    body: JSON.stringify(obj),
    headers: {
      "Content-Type": "application/json",
      "Cache-Control": "no-store, no-cache, must-revalidate",
      Pragma: "no-cache",
    },
  };

  let res = null;
  try {
    res = await fetchWithTimeout(
      buildApiUrl("/login", base),
      requestOptions,
      LOGIN_REQUEST_TIMEOUT_MS,
    );
  } catch (networkError) {
    const isTimeoutError = networkError?.name === "AbortError";
    const error = new Error(
      isTimeoutError
        ? "Login service timed out. The server is not responding. Please retry."
        : "Unable to reach login service. Please check your connection and retry.",
    );
    error.status = 0;
    error.payload = {
      message: networkError?.message || "",
      code: isTimeoutError ? "LOGIN_TIMEOUT" : "NETWORK_ERROR",
    };
    throw error;
  }
  const normalized = normalizeLoginPayload(await parseJsonSafe(res));
  if (res.ok) return normalized;
  const error = new Error(
    extractLoginErrorMessage(normalized) || "Login failed.",
  );
  error.status = res.status;
  error.payload = normalized;
  throw error;
};

export const fetch_session = async (options = {}) => {
  const { forceRefresh = false } = options;
  const token = getToken();
  const portalMode = readActivePortalMode();
  if (
    !forceRefresh &&
    token &&
    sessionCache.token === token &&
    sessionCache.portalMode === portalMode &&
    sessionCache.data
  ) {
    return sessionCache.data;
  }
  if (
    sessionPromise &&
    sessionPromiseToken === token &&
    sessionPromisePortalMode === portalMode
  ) {
    return sessionPromise;
  }
  if (!token) {
    sessionCache = { token: "", portalMode: "", data: null };
  }
  const fetchPromise = (async () => {
    const query = buildQuery({ _ts: Date.now() });
    const paths = ["/api/session"];
    const attemptedUrls = new Set();
    let lastResponse = null;
    const hasAuthSessionShape = (payload = {}) =>
      Boolean(
        payload?.role ||
        payload?.admin_data ||
        payload?.teacher_data ||
        payload?.student_data ||
        payload?.parent_data ||
        (payload?.user &&
          (payload?.user?.role ||
            payload?.user?._id ||
            payload?.user?.id ||
            payload?.user?.userId ||
            payload?.user?.email)),
      );

    for (const path of paths) {
      const requestPath = `${path}${query}`;
      const requestUrl = buildApiUrl(requestPath, base);
      if (attemptedUrls.has(requestUrl)) continue;
      attemptedUrls.add(requestUrl);

      const res = await erpFetch(requestPath, {
        method: "GET",
        cache: "no-store",
        timeoutMs: AUTH_REQUEST_TIMEOUT_MS,
        suppressGlobalError: true,
        headers: {
          "Content-Type": "application/json",
          "Cache-Control": "no-store, no-cache, must-revalidate",
          Pragma: "no-cache",
        },
      });
      lastResponse = res;
      const ans = normalizeSessionEnvelope(await parseJsonSafe(res));
      const normalized = { ...ans, _httpStatus: res.status };
      if (res.ok) {
        if (hasAuthSessionShape(ans)) {
          if (token) {
            sessionCache = { token, portalMode, data: normalized };
          }
          return normalized;
        }
        return { ...normalized, _httpStatus: 502 };
      }
      if (![404, 405].includes(res.status)) {
        return normalized;
      }
    }
    const fallback = lastResponse
      ? normalizeSessionEnvelope(await parseJsonSafe(lastResponse))
      : {};
    return { ...fallback, _httpStatus: lastResponse?.status || 404 };
  })();

  sessionPromise = fetchPromise;
  sessionPromiseToken = token || "";
  sessionPromisePortalMode = portalMode;
  try {
    return await fetchPromise;
  } finally {
    if (sessionPromise === fetchPromise) {
      sessionPromise = null;
      sessionPromiseToken = "";
      sessionPromisePortalMode = "";
    }
  }
};

const normalizeActiveSessionEnvelope = (payload = {}) => {
  if (!payload || typeof payload !== "object") return payload;
  if (payload.session) return payload;
  const current = payload.current || payload.active || null;
  if (current) return { ...payload, session: current };
  if (Array.isArray(payload.sessions)) {
    const flagged =
      payload.sessions.find((row) => row?.isCurrent) || payload.sessions[0];
    if (flagged) return { ...payload, session: flagged };
  }
  return payload;
};

export const fetchActiveAcademicSession = async () => {
  const query = buildQuery({ _ts: Date.now() });
  const requestOptions = {
    method: "GET",
    cache: "no-store",
    timeoutMs: AUTH_REQUEST_TIMEOUT_MS,
    suppressGlobalError: true,
    headers: {
      "Content-Type": "application/json",
    },
  };
  const attemptedUrls = new Set();
  const activePaths = [`/api/academic-sessions/active`];

  const fetchSessionPayload = async (path) => {
    const requestPath = `${path}${query}`;
    const requestUrl = buildApiUrl(requestPath, base);
    if (attemptedUrls.has(requestUrl)) {
      return null;
    }
    attemptedUrls.add(requestUrl);
    const res = await erpFetch(requestPath, requestOptions);
    const ans = await parseJsonSafe(res);
    return { res, ans };
  };

  let lastResp = null;
  for (const path of activePaths) {
    const activeResp = await fetchSessionPayload(path);
    if (!activeResp) continue;
    lastResp = activeResp;

    const normalized = normalizeActiveSessionEnvelope(activeResp.ans || {});
    if (activeResp.res.ok && normalized?.session) {
      return normalized;
    }

    if (
      activeResp.res.status === 304 ||
      [404, 405].includes(activeResp.res.status) ||
      (activeResp.res.ok && !normalized?.session)
    ) {
      continue;
    }

    const error = new Error(
      activeResp.ans?.message ||
        activeResp.ans?.error ||
        "Failed to fetch active academic session",
    );
    error.status = activeResp.res.status;
    error.payload = activeResp.ans;
    throw error;
  }

  const error = new Error(
    lastResp?.ans?.message ||
      lastResp?.ans?.error ||
      "Failed to fetch active academic session",
  );
  error.status = lastResp?.res?.status || 404;
  error.payload = lastResp?.ans || {};
  throw error;
};

export const fetchSchoolSessions = async ({
  schoolId,
  adminId,
  academicYearStartMonth,
  academicYearStartDay,
  suppressGlobalError,
} = {}) => {
  const query = buildQuery({
    schoolId: schoolId || adminId,
    academicYearStartMonth,
    academicYearStartDay,
  });
  const res = await erpFetch(`/api/sessions${query}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    suppressGlobalError,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to fetch school sessions",
    );
  }
  return ans;
};

export const fetchActiveSchoolSessions = async ({ schoolId, adminId } = {}) => {
  const resolvedSchoolId = String(schoolId || adminId || "").trim();
  const cacheKey = resolvedSchoolId || "__default__";
  if (activeSessionsCache.has(cacheKey)) {
    return activeSessionsCache.get(cacheKey);
  }

  const requestOptions = {
    method: "GET",
    suppressGlobalError: true,
    headers: {
      "Content-Type": "application/json",
    },
  };

  const normalizeSessionItem = (item, flags = {}) => {
    if (!item || typeof item !== "object") return null;
    const id = String(
      item?.id ||
        item?._id ||
        item?.sessionId ||
        item?.code ||
        item?.label ||
        "",
    ).trim();
    const label = String(
      item?.label || item?.name || item?.academicYear || item?.code || "",
    ).trim();
    if (!id || !label) return null;
    return {
      ...item,
      id,
      label,
      isCurrent: Boolean(
        item?.isCurrent || item?.is_current || flags.isCurrent,
      ),
      isNext: Boolean(item?.isNext || item?.is_next || flags.isNext),
    };
  };

  const normalizeActivePayload = (payload = {}) => {
    const currentFromSession = normalizeSessionItem(payload?.session, {
      isCurrent: true,
    });
    const current =
      currentFromSession ||
      normalizeSessionItem(payload?.current, { isCurrent: true });
    const next = normalizeSessionItem(payload?.next, { isNext: true });
    let sessions = Array.isArray(payload?.sessions)
      ? payload.sessions.map((item) => normalizeSessionItem(item))
      : [];
    sessions = sessions.filter(Boolean);
    if (!sessions.length) {
      if (current) sessions.push(current);
      if (next) sessions.push(next);
    }
    return {
      schoolId: payload?.schoolId || resolvedSchoolId || "",
      schoolToday: String(payload?.schoolToday || "").trim(),
      sessions,
      current,
      next,
    };
  };

  const path = resolvedSchoolId
    ? `/api/schools/${encodeURIComponent(resolvedSchoolId)}/sessions/active`
    : "/api/academic-sessions/active";
  const res = await erpFetch(path, requestOptions);
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    const error = new Error(
      ans?.message || ans?.error || "Failed to fetch active school sessions",
    );
    error.status = res.status;
    error.payload = ans;
    throw error;
  }
  const normalized = normalizeActivePayload(ans);
  activeSessionsCache.set(cacheKey, normalized);
  return normalized;
};

export const createInitialSchoolSession = async ({
  schoolId,
  academicYearStart,
}) => {
  const res = await erpFetch(
    `/api/schools/${encodeURIComponent(schoolId)}/sessions/initial`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ academicYearStart }),
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw new Error(
      ans?.message || "Failed to set the initial school academic year.",
    );
  activeSessionsCache.delete(String(schoolId || "").trim() || "__default__");
  return ans;
};

export const register_student = async (obj) => {
  const command = prepareStudentCreationCommand(obj);
  const res = await erpFetch(`/api/students`, {
    method: "POST",
    body: JSON.stringify(command.payload),
    headers: {
      "Content-Type": "application/json",
      "Idempotency-Key": command.idempotencyKey,
    },
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    const error = new Error(
      ans?.message || ans?.error || "Failed to add student",
    );
    error.status = res.status;
    error.payload = ans;
    throw error;
  }
  return ans;
};

export const fetchAdmissionEligibilityPreview = async (params = {}) => {
  const query = buildQuery({
    schoolId: params.schoolId,
    sessionId: params.sessionId,
    session: params.session,
    dob: params.dob,
    boardType: params.boardType,
  });
  const res = await erpFetch(`/api/admissions/eligibility-preview${query}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    const error = new Error(
      ans?.message || ans?.error || "Failed to load admission guidance",
    );
    error.status = res.status;
    error.payload = ans;
    throw error;
  }
  return ans;
};

export const fetchStudentIdPreview = async (params = {}) => {
  const query = buildQuery({
    schoolId: params.schoolId,
    sessionId: params.sessionId,
    session: params.session,
    classId: params.classId,
    sectionId: params.sectionId,
  });
  const res = await erpFetch(`/api/students/id-preview${query}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    const error = new Error(
      ans?.message || ans?.error || "Failed to load student ID preview",
    );
    error.status = res.status;
    error.payload = ans;
    throw error;
  }
  return ans;
};

export const getStudents = async (params = {}) => {
  const query = buildQuery({
    q: params.q,
    search: params.search,
    class: params.class,
    className: params.className,
    classId: params.classId,
    class_id: params.class_id,
    section: params.section,
    sectionId: params.sectionId,
    section_id: params.section_id,
    session: params.session,
    sessionId: params.sessionId,
    session_id: params.session_id,
    lifecycleState: params.lifecycleState,
    date: params.date,
    page: params.page,
    limit: params.limit,
  });
  const res = await erpFetch(`/api/students${query}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    suppressGlobalError: params.suppressGlobalError,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    const error = new Error(
      ans?.message || ans?.error || "Failed to fetch students",
    );
    error.status = res.status;
    error.payload = ans;
    throw error;
  }
  const students = Array.isArray(ans?.students)
    ? ans.students
    : Array.isArray(ans?.items)
      ? ans.items
      : Array.isArray(ans)
        ? ans
        : [];
  const total =
    Number(ans?.total ?? ans?.totalCount ?? ans?.count) || students.length;
  return {
    students,
    total,
    page: Number(ans?.page || params.page || 1),
    limit: Number(ans?.limit || params.limit || students.length),
  };
};

export const correctStaffAttendance = async (payload = {}) => {
  const res = await erpFetch(`/api/staff-attendance/corrections`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to correct staff attendance",
    );
  }
  return ans;
};

export const searchStudentsByFamily = async (queryText, params = {}) => {
  const query = buildQuery({ q: queryText, limit: params.limit });
  const res = await erpFetch(`/api/students/search${query}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    suppressGlobalError: params.suppressGlobalError,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    const error = new Error(
      ans?.message || ans?.error || "Failed to search students and families",
    );
    error.status = res.status;
    error.payload = ans;
    throw error;
  }
  return {
    authority: ans?.authority || "",
    results: Array.isArray(ans?.results) ? ans.results : [],
    count: Number(ans?.count) || 0,
  };
};

export const getStudentProfile = async (studentId, options = {}) => {
  const id = String(studentId || "").trim();
  if (!id) throw new Error("studentId is required");
  const res = await erpFetch(`/api/students/${encodeURIComponent(id)}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    suppressGlobalError: options?.suppressGlobalError,
    cacheTtlMs: options?.cacheTtlMs === 0 ? 0 : options?.cacheTtlMs || 60000,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    if (isOptionalStatus(res, options)) return null;
    throw new Error(
      ans?.message || ans?.error || "Failed to fetch student profile",
    );
  }
  return ans?.student || ans;
};

export const updateStudentProfile = async (studentId, payload = {}) => {
  const id = String(studentId || "").trim();
  if (!id) throw new Error("studentId is required");
  const res = await erpFetch(`/api/students/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    const error = new Error(
      ans?.message || ans?.error || "Failed to update student",
    );
    error.status = res.status;
    error.payload = ans;
    throw error;
  }
  return ans?.student || ans;
};

export const withdrawStudent = async (studentId, command = {}) => {
  const id = String(studentId || "").trim();
  if (!id) throw new Error("studentId is required");
  const effectiveDate = String(command.effectiveDate || "").trim();
  const reasonCode = String(command.reasonCode || command.reason || "").trim();
  const idempotencyKey = String(command.idempotencyKey || "").trim();
  if (!effectiveDate || !reasonCode || !idempotencyKey) {
    throw new Error(
      "Effective withdrawal date, reason and command reference are required",
    );
  }
  const res = await erpFetch(
    `/api/students/${encodeURIComponent(id)}/withdraw`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Idempotency-Key": idempotencyKey,
      },
      body: JSON.stringify({ effectiveDate, reasonCode }),
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    const error = new Error(
      ans?.message || ans?.error || "Failed to withdraw student",
    );
    error.status = res.status;
    error.payload = ans;
    throw error;
  }
  return ans;
};

export const readmitStudent = async (studentId, command = {}) => {
  const id = String(studentId || "").trim();
  if (!id) throw new Error("studentId is required");
  const payload = {
    targetSessionId: String(command.targetSessionId || "").trim(),
    targetClassId: String(command.targetClassId || "").trim(),
    targetSectionId: String(command.targetSectionId || "").trim() || null,
    effectiveDate: String(command.effectiveDate || "").trim(),
    reasonCode: String(command.reasonCode || "").trim(),
    password: String(command.password || ""),
  };
  const idempotencyKey = String(command.idempotencyKey || "").trim();
  if (
    !payload.targetSessionId ||
    !payload.targetClassId ||
    !payload.effectiveDate ||
    !payload.reasonCode ||
    !payload.password ||
    !idempotencyKey
  ) {
    throw new Error(
      "Session, class, readmission date, reason, new password and command reference are required",
    );
  }
  const res = await erpFetch(
    `/api/students/${encodeURIComponent(id)}/readmit`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Idempotency-Key": idempotencyKey,
      },
      body: JSON.stringify(payload),
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    const error = new Error(
      ans?.message || ans?.error || "Failed to readmit student",
    );
    error.status = res.status;
    error.payload = ans;
    throw error;
  }
  return ans;
};

export const permanentlyDeleteUnusedStudent = async (
  studentId,
  command = {},
) => {
  const id = String(studentId || "").trim();
  if (!id) throw new Error("studentId is required");
  const confirmation = String(command.confirmation || "").trim();
  const reason = String(command.reason || "").trim();
  if (confirmation !== "DELETE PERMANENTLY" || !reason) {
    throw new Error("Type DELETE PERMANENTLY and give a reason to continue");
  }
  const res = await erpFetch(
    `/api/students/${encodeURIComponent(id)}/permanent`,
    {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ confirmation, reason }),
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    const error = new Error(
      ans?.message || ans?.error || "Permanent deletion failed",
    );
    error.status = res.status;
    error.payload = ans;
    throw error;
  }
  return ans;
};

export const fetchStudentBoosterScore = async (studentId, options = {}) => {
  const id = String(studentId || "").trim();
  if (!id) throw new Error("studentId is required");
  const res = await erpFetch(
    `/api/students/${encodeURIComponent(id)}/booster`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      suppressGlobalError: options?.suppressGlobalError,
      cacheTtlMs: options?.cacheTtlMs === 0 ? 0 : options?.cacheTtlMs || 60000,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    if (isOptionalStatus(res, options)) {
      if (isSeedEnabled()) {
        return seedBoosterScore(id);
      }
      return { available: false };
    }
    throw buildStatusError(res, ans, "Failed to load booster score");
  }
  if (isSeedEnabled()) {
    const hasScore =
      typeof ans === "number" ||
      typeof ans?.score === "number" ||
      typeof ans?.scores === "number" ||
      typeof ans?.data?.score === "number" ||
      typeof ans?.data?.scores === "number" ||
      (ans?.score && typeof ans.score === "object") ||
      (ans?.scores && typeof ans.scores === "object");
    if (
      !hasScore &&
      (ans?.available === false || Object.keys(ans || {}).length === 0)
    ) {
      return seedBoosterScore(id);
    }
  }
  return ans;
};

export const fetchBoosterManagementStudentsSummary = async (
  params = {},
  options = {},
) => {
  const baseParams = {
    schoolId: params.schoolId || params.adminId,
    classId: params.classId,
    sectionId: params.sectionId,
    className: params.className,
    class: params.class,
    section: params.section,
    from: params.from,
    to: params.to,
    page: params.page,
    pageSize: params.pageSize,
  };
  const fetchPage = async (pageNumber = params.page || 1) => {
    const query = buildQuery({
      ...baseParams,
      page: pageNumber,
    });
    const res = await erpFetch(
      `/api/booster-v2/management/students-summary${query}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        suppressGlobalError: options?.suppressGlobalError,
      },
    );
    const ans = await parseJsonSafe(res);
    if (!res.ok) {
      if (isOptionalStatus(res, options)) {
        return normalizeBoosterManagementStudentsSummaryResponse(
          { available: false, students: [], summary: {}, dashboard: {} },
          { page: baseParams.page, pageSize: baseParams.pageSize },
        );
      }
      throw buildStatusError(
        res,
        ans,
        "Failed to load booster performance summary",
      );
    }
    return normalizeBoosterManagementStudentsSummaryResponse(ans, {
      page: baseParams.page,
      pageSize: baseParams.pageSize,
    });
  };

  const firstPage = await fetchPage(baseParams.page);
  if (!options?.fetchAllPages || firstPage.pagination.totalPages <= 1) {
    return firstPage;
  }

  const remainingPages = await Promise.all(
    Array.from(
      { length: Math.max(0, firstPage.pagination.totalPages - 1) },
      (_, index) => fetchPage(index + 2),
    ),
  );

  return {
    ...firstPage,
    students: [firstPage, ...remainingPages].flatMap((page) => page.students),
  };
};

export const fetchBoosterClassTeachers = async (params = {}, options = {}) => {
  const query = buildQuery({ schoolId: params.schoolId || params.adminId });
  const res = await erpFetch(`/api/booster-v2/class-teachers${query}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    suppressGlobalError: options?.suppressGlobalError,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    if (isOptionalStatus(res, options)) {
      return { available: false, classTeachers: [] };
    }
    throw buildStatusError(res, ans, "Failed to load booster class teachers");
  }
  return {
    available: ans?.available !== false,
    classTeachers: Array.isArray(ans?.classTeachers)
      ? ans.classTeachers
      : Array.isArray(ans?.data?.classTeachers)
        ? ans.data.classTeachers
        : [],
  };
};

export const fetchBoosterTeacherDashboard = async (
  params = {},
  options = {},
) => {
  const classId = String(params.classId || params.class_id || "").trim();
  if (!classId) throw new Error("classId is required");
  const query = buildQuery({
    schoolId: params.schoolId || params.adminId,
    section: params.sectionId || params.section || params.sectionName,
    from: params.from,
    to: params.to,
    recentDays: params.recentDays,
  });
  const res = await erpFetch(
    `/api/booster-v2/classes/${encodeURIComponent(classId)}/teacher-dashboard${query}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      suppressGlobalError: options?.suppressGlobalError,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    if (isOptionalStatus(res, options)) {
      return {
        available: false,
        rows: [],
        students: [],
        summary: {},
        leaderboards: {},
        lists: {},
      };
    }
    throw buildStatusError(
      res,
      ans,
      "Failed to load booster teacher dashboard",
    );
  }
  return ans;
};

export const requestAdmissionOverride = async (payload = {}) => {
  const res = await erpFetch(`/api/admissions/overrides`, {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      "Content-Type": "application/json",
    },
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to request director approval",
    );
  }
  return ans;
};

export const fetchAdmissionOverride = async (overrideId) => {
  const id = String(overrideId || "").trim();
  if (!id) {
    throw new Error("overrideId is required");
  }
  const res = await erpFetch(
    `/api/admissions/overrides/${encodeURIComponent(id)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to fetch override status",
    );
  }
  return ans;
};

export const all_admins = async () => {
  const res = await erpFetch(`/admins`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
  const ans = await res.json();
  return ans;
};

export const search_students = async (_adminId, params = {}) => {
  const resp = await getStudents({
    q: params?.q || params?.search,
    class: params?.class,
    className: params?.className,
    section: params?.section,
    session: params?.session,
    page: params?.page,
    limit: params?.limit,
  });
  return { students: resp.students, total: resp.total };
};

export const studentUpdate = async (id, dataToUpdate) => {
  const updated = await updateStudentProfile(id, dataToUpdate);
  return { student: updated, resp: updated };
};

export const searchFeeStudents = async (adminId, query, options = {}) => {
  const params = {
    adminId,
    q: query,
    ...options,
  };
  const res = await erpFetch(`/api/fees/student/search${buildQuery(params)}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(ans?.message || ans?.error || "Failed to search students");
  }
  return ans;
};

export const getFeeStudentDue = async (adminId, studentId, options = {}) => {
  const id = String(studentId || "").trim();
  if (!id) {
    throw new Error("studentId is required");
  }
  const {
    suppressGlobalError,
    optionalStatuses,
    allowNotFound,
    ...queryOptions
  } = options || {};
  const params = {
    adminId,
    ...queryOptions,
  };
  const res = await erpFetch(
    `/api/fees/student/${encodeURIComponent(id)}/due${buildQuery(params)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      suppressGlobalError,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    if (isOptionalStatus(res, { optionalStatuses, allowNotFound })) {
      return {
        available: false,
        totals: { outstanding: 0, advance: 0, netDue: 0 },
      };
    }
    throw buildStatusError(res, ans, "Failed to load student due");
  }
  return ans;
};

export const getStaff = async (adminId, options = {}) => {
  const res = await erpFetch(`/getStaff/${adminId}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    suppressGlobalError: options?.suppressGlobalError,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    if (isOptionalStatus(res, options)) return [];
    throw buildStatusError(res, ans, "Failed to load staff");
  }
  return ans;
};

export const fetchParentSchoolWeek = async (params = {}, options = {}) => {
  const response = await erpFetch(
    `/api/portal/school-week${buildQuery({
      schoolId: params.schoolId || params.adminId || undefined,
      className: params.className || undefined,
      classKey: params.classKey || params.classToken || undefined,
      section: params.section || undefined,
      session: params.session || undefined,
      sessionKey: params.sessionKey || undefined,
      studentId: params.studentId || undefined,
    })}`,
    {
      method: "GET",
      suppressGlobalError: options?.suppressGlobalError,
      cacheTtlMs: options?.cacheTtlMs === 0 ? 0 : options?.cacheTtlMs,
    },
  );
  const payload = await parseJsonSafe(response);
  if (!response.ok) {
    throw buildStatusError(
      response,
      payload,
      "School week couldn't be updated right now.",
    );
  }
  return payload || null;
};

export const getTimetableConfig = async (schoolId, options = {}) => {
  const res = await erpFetch(
    `/api/timetable/config${buildQuery({
      schoolId,
      classId: options?.classId,
      class: options?.className,
    })}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      suppressGlobalError: true,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to load timetable config",
    );
  }
  return ans;
};

export const saveTimetableConfig = async (payload = {}) => {
  const res = await erpFetch(`/api/timetable/config`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    suppressGlobalError: true,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to save timetable config",
    );
  }
  return ans;
};

// Phase 1 academic setup APIs
export const getSetupStatus = async () => {
  const res = await erpFetch(`/api/schools/me/setup-status`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to load setup status",
    );
  }
  return ans;
};

export const updateSectionsEnabled = async (enabled) => {
  const res = await erpFetch(`/api/schools/me/sections`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ has_sections: Boolean(enabled) }),
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to update sections setting",
    );
  }
  return ans;
};

// Management setup APIs
export const getManagementProfile = async () => {
  const res = await erpFetch(`/api/management/profile`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to load school profile",
    );
  }
  return ans;
};

export const updateManagementProfile = async (payload = {}) => {
  const res = await erpFetch(`/api/management/profile`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to update school profile",
    );
  }
  return ans;
};

export const getClasses = async () => {
  const res = await erpFetch(`/api/classes`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(ans?.message || ans?.error || "Failed to load classes");
  }
  return ans;
};

export const createClass = async (payload = {}) => {
  const res = await erpFetch(`/api/classes`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(ans?.message || ans?.error || "Failed to create class");
  }
  return ans;
};

export const updateClass = async (classId, payload = {}) => {
  const res = await erpFetch(`/api/classes/${encodeURIComponent(classId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(ans?.message || ans?.error || "Failed to update class");
  }
  return ans;
};

export const deleteClass = async (classId) => {
  const res = await erpFetch(`/api/classes/${encodeURIComponent(classId)}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(ans?.message || ans?.error || "Failed to delete class");
  }
  return ans;
};

export const getSections = async () => {
  const res = await erpFetch(`/api/sections`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(ans?.message || ans?.error || "Failed to load sections");
  }
  return ans;
};

export const createSection = async (payload = {}) => {
  const res = await erpFetch(`/api/sections`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(ans?.message || ans?.error || "Failed to create section");
  }
  return ans;
};

export const updateSection = async (sectionId, payload = {}) => {
  const res = await erpFetch(`/api/sections/${encodeURIComponent(sectionId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(ans?.message || ans?.error || "Failed to update section");
  }
  return ans;
};

export const deleteSection = async (sectionId) => {
  const res = await erpFetch(`/api/sections/${encodeURIComponent(sectionId)}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(ans?.message || ans?.error || "Failed to delete section");
  }
  return ans;
};

export const getClassSubjects = async (classId, { sessionId = "" } = {}) => {
  const res = await erpFetch(
    `/api/classes/${encodeURIComponent(classId)}/subjects${buildQuery({ sessionId })}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to load class subjects",
    );
  }
  return ans;
};

export const saveClassSubjects = async (
  classId,
  subjectIds = [],
  { sessionId = "" } = {},
) => {
  const res = await erpFetch(
    `/api/classes/${encodeURIComponent(classId)}/subjects`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ subjectIds, sessionId }),
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to save class subjects",
    );
  }
  return ans;
};

const requestTeacherApi = async (
  suffix = "",
  options = {},
  errorMessage = "Teacher request failed",
) => {
  const res = await erpFetch(`/api/teachers${suffix}`, options);
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    const error = new Error(ans?.message || ans?.error || errorMessage);
    error.status = res.status;
    error.payload = ans;
    throw error;
  }
  return ans;
};

export const getTeachers = (params = {}) =>
  requestTeacherApi(
    `${buildQuery(params)}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      suppressGlobalError: true,
    },
    "Failed to load teachers",
  );

export const getTeacher = async (teacherId) => {
  const id = String(teacherId || "").trim();
  if (!id) {
    throw new Error("teacherId is required");
  }
  return requestTeacherApi(
    `/${encodeURIComponent(id)}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      suppressGlobalError: true,
    },
    "Failed to load teacher",
  );
};

export const getTeacherWorkSummary = async (teacherId) => {
  const id = String(teacherId || "").trim();
  if (!id) throw new Error("teacherId is required");
  const res = await erpFetch(
    `/api/teachers/${encodeURIComponent(id)}/work-summary`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      suppressGlobalError: true,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to load teacher work summary",
    );
  }
  return ans;
};

export const getStaffLogins = async (params = {}) => {
  const res = await erpFetch(`/api/staff/logins${buildQuery(params)}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    suppressGlobalError: true,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to load staff logins",
    );
  }
  return ans;
};

export const createStaffLogin = async (payload = {}) => {
  const res = await erpFetch(`/api/staff/logins/create`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    suppressGlobalError: true,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to create staff login",
    );
  }
  return ans;
};

export const updateStaffLoginPassword = async (userId, payload = {}) => {
  const res = await erpFetch(
    `/api/staff/logins/${encodeURIComponent(userId)}/password`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      suppressGlobalError: true,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to reset staff password",
    );
  }
  return ans;
};

export const updateStaffLoginStatus = async (userId, payload = {}) => {
  const res = await erpFetch(
    `/api/staff/logins/${encodeURIComponent(userId)}/status`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      suppressGlobalError: true,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to update staff login status",
    );
  }
  return ans;
};

export const getManagementPositions = async (params = {}) => {
  const res = await erpFetch(
    `/api/settings/management-positions${buildQuery(params)}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      suppressGlobalError: true,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to load management positions",
    );
  }
  return ans;
};

export const assignManagementPosition = async (positionKey, payload = {}) => {
  const res = await erpFetch(
    `/api/settings/management-positions/${encodeURIComponent(positionKey)}/assign`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      suppressGlobalError: true,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to assign management position",
    );
  }
  return ans;
};

export const unassignManagementPosition = async (positionKey, payload = {}) => {
  const res = await erpFetch(
    `/api/settings/management-positions/${encodeURIComponent(positionKey)}/unassign`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      suppressGlobalError: true,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to unassign management position",
    );
  }
  return ans;
};

export const updateManagementPositionStatus = async (
  positionKey,
  payload = {},
) => {
  const res = await erpFetch(
    `/api/settings/management-positions/${encodeURIComponent(positionKey)}/status`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      suppressGlobalError: true,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to update management position",
    );
  }
  return ans;
};

export const updateManagementPositionPermissions = async (
  positionKey,
  payload = {},
) => {
  const res = await erpFetch(
    `/api/settings/management-positions/${encodeURIComponent(positionKey)}/permissions`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      suppressGlobalError: true,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to update position permissions",
    );
  }
  return ans;
};

export const getClassTeacherAssignments = async (params = {}) => {
  const res = await erpFetch(
    `/api/academics/class-teachers${buildQuery(params)}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      suppressGlobalError: true,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to load class teacher assignments",
    );
  }
  return ans;
};

export const assignClassTeacher = async (className, payload = {}) => {
  const res = await erpFetch(
    `/api/academics/class-teachers/${encodeURIComponent(className)}/assign`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      suppressGlobalError: true,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to assign class teacher",
    );
  }
  return ans;
};

export const unassignClassTeacher = async (className, payload = {}) => {
  const res = await erpFetch(
    `/api/academics/class-teachers/${encodeURIComponent(className)}/unassign`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      suppressGlobalError: true,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to unassign class teacher",
    );
  }
  return ans;
};

export const createTeacher = async (payload = {}) => {
  const sanitized = { ...(payload || {}) };
  if (sanitized.username && !sanitized.userId && !sanitized.user_id) {
    sanitized.userId = sanitized.username;
  }
  if (sanitized.userId && !sanitized.username) {
    sanitized.username = sanitized.userId;
  }
  return requestTeacherApi(
    ``,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(sanitized),
      suppressGlobalError: true,
    },
    "Failed to create teacher",
  );
};

export const updateTeacher = async (teacherId, payload = {}) => {
  const id = String(teacherId || "").trim();
  if (!id) {
    throw new Error("teacherId is required");
  }
  return requestTeacherApi(
    `/${encodeURIComponent(id)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      suppressGlobalError: true,
    },
    "Failed to update teacher",
  );
};

export const deleteTeacher = async (teacherId) => {
  const id = String(teacherId || "").trim();
  if (!id) {
    throw new Error("teacherId is required");
  }
  return requestTeacherApi(
    `/${encodeURIComponent(id)}`,
    {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      suppressGlobalError: true,
    },
    "Failed to archive teacher",
  );
};

export const uploadTeacherDocuments = async (teacherId, formData) => {
  const id = String(teacherId || "").trim();
  if (!id) {
    throw new Error("teacherId is required");
  }
  if (!(formData instanceof FormData)) {
    throw new Error("FormData is required");
  }
  return requestTeacherApi(
    `/${encodeURIComponent(id)}/documents`,
    {
      method: "POST",
      body: formData,
      suppressGlobalError: true,
    },
    "Failed to upload teacher documents",
  );
};

export const getTeacherSelfProfile = async () => {
  const res = await erpFetch(`/api/teacher/profile`, {
    method: "GET",
    suppressGlobalError: true,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to load teacher profile",
    );
  }
  return ans;
};

export const fetchTeacherAssignments = async (options = {}) => {
  const res = await erpFetch(`/api/teacher/assignments`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    suppressGlobalError: options?.suppressGlobalError,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    if (isOptionalStatus(res, options)) {
      return {
        teacherUserId: "",
        classTeacher: null,
        classes: [],
        subjects: [],
        available: false,
      };
    }
    throw new Error(
      ans?.message || ans?.error || "Failed to load teacher assignments",
    );
  }
  return ans;
};

export const fetchTeacherParentConversations = async (
  params = {},
  options = {},
) => {
  const res = await erpFetch(
    `/api/teacher/parent-conversations${buildQuery(params)}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      suppressGlobalError: options?.suppressGlobalError ?? true,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw buildStatusError(
      res,
      ans,
      "Unable to load parent conversations right now.",
    );
  }
  return ans;
};

export const fetchTeacherDayContext = async (options = {}) => {
  const query = buildQuery({
    date: options?.date,
    sessionId: options?.sessionId,
  });
  const res = await erpFetch(`/api/teacher/day-context${query}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    suppressGlobalError: options?.suppressGlobalError ?? true,
    cacheTtlMs: options?.cacheTtlMs === undefined ? 15000 : options.cacheTtlMs,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw buildStatusError(
      res,
      ans,
      "Unable to load today’s class status right now.",
    );
  }
  return ans;
};

export const saveTeacherAskYourChild = async (payload = {}, options = {}) => {
  const res = await erpFetch(`/api/teacher/ask-your-child`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    suppressGlobalError: options?.suppressGlobalError ?? true,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw buildStatusError(res, ans, "Unable to publish Ask Your Child.");
  }
  return ans;
};

export const updateTeacherSelfProfile = async (payload = {}) => {
  const res = await erpFetch(`/api/teacher/profile`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    suppressGlobalError: true,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to update teacher profile",
    );
  }
  return ans;
};

export const uploadTeacherSelfDocuments = async (formData) => {
  if (!(formData instanceof FormData)) {
    throw new Error("FormData is required");
  }
  const res = await erpFetch(`/api/teacher/documents`, {
    method: "POST",
    body: formData,
    suppressGlobalError: true,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to upload teacher documents",
    );
  }
  return ans;
};

export const submitTeacherProfileForVerification = async () => {
  const res = await erpFetch(`/api/teacher/submit`, {
    method: "POST",
    suppressGlobalError: true,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    const error = new Error(
      ans?.message || ans?.error || "Failed to submit teacher profile",
    );
    error.payload = ans;
    throw error;
  }
  return ans;
};

export const getAssignments = async (classId, { sessionId = "" } = {}) => {
  const res = await erpFetch(
    `/api/assignments${buildQuery({ classId, sessionId })}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(ans?.message || ans?.error || "Failed to load assignments");
  }
  return ans;
};

export const getAssignmentIntegrity = async ({ sessionId = "" } = {}) => {
  const res = await erpFetch(
    `/api/teacher-allocation/integrity${buildQuery({ sessionId })}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      suppressGlobalError: true,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message ||
        ans?.error ||
        "Failed to load teacher assignment integrity",
    );
  }
  return ans;
};

export const saveAssignments = async (classId, payload = {}) => {
  const res = await erpFetch(
    `/api/assignments/${encodeURIComponent(classId)}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(ans?.message || ans?.error || "Failed to save assignments");
  }
  return ans;
};

export const getClassTimetable = async (classId, { sessionId = "" } = {}) => {
  const res = await erpFetch(
    `/api/timetable${buildQuery({ classId, sessionId })}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      suppressGlobalError: true,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(ans?.message || ans?.error || "Failed to load timetable");
  }
  return ans;
};

export const saveClassTimetable = async (classId, payload = {}) => {
  const res = await erpFetch(`/api/timetable/${encodeURIComponent(classId)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    suppressGlobalError: true,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    const error = new Error(
      ans?.message || ans?.error || "Failed to save timetable",
    );
    error.code = ans?.code || "";
    throw error;
  }
  return ans;
};

export const fetchPreschoolTimetableUnmatched = async ({ classId } = {}) => {
  const res = await erpFetch(
    `/api/timetable/preschool/unmatched${buildQuery({ classId })}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      suppressGlobalError: true,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to load preschool unmatched report",
    );
  }
  return ans;
};

// Subject Master API (optional backend support)
export const getSubjects = async ({ schoolId, classId, sessionId } = {}) => {
  const res = await erpFetch(
    `/api/subjects${buildQuery({ schoolId, classId, sessionId })}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(ans?.message || ans?.error || "Failed to load subjects");
  }
  return ans;
};

export const createSubject = async (payload = {}) => {
  const res = await erpFetch(`/api/subjects`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(ans?.message || ans?.error || "Failed to create subject");
  }
  return ans;
};

export const updateSubject = async (subjectId, payload = {}) => {
  const res = await erpFetch(`/api/subjects/${subjectId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(ans?.message || ans?.error || "Failed to update subject");
  }
  return ans;
};

export const deleteSubject = async (subjectId) => {
  const res = await erpFetch(`/api/subjects/${subjectId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ is_active: false }),
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(ans?.message || ans?.error || "Failed to delete subject");
  }
  return ans;
};

export const deleteSubjectHard = async (subjectId) => {
  const res = await erpFetch(`/api/subjects/${subjectId}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(ans?.message || ans?.error || "Failed to delete subject");
  }
  return ans;
};

export const getTeacherTimeTable = async (teacherId, options = {}) => {
  const resolvedTeacherId = String(teacherId || "").trim();
  if (!resolvedTeacherId) {
    throw new Error("teacherId is required");
  }
  const fallbackSchoolId = String(
    options.schoolId || resolveSchoolIdHeader() || "",
  ).trim();
  try {
    const response = await erpFetch(
      `/api/timetable/teacher/${encodeURIComponent(resolvedTeacherId)}${buildQuery({ schoolId: fallbackSchoolId })}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        suppressGlobalError: true,
        cacheTtlMs:
          options?.cacheTtlMs === 0 ? 0 : options?.cacheTtlMs || 60000,
      },
    );

    const data = await parseJsonSafe(response);
    if (response.ok) {
      return data;
    }
    if ([403, 404].includes(response.status)) {
      return { days: [] };
    }
    throw new Error(
      data?.message || data?.error || "Failed to fetch timetable",
    );
  } catch (error) {
    throw new Error("Error fetching timetable: " + error.message);
  }
};

export const saveUploads_lostAndFound = async (adminId, formData) => {
  try {
    const response = await erpFetch(`/lostFound/${adminId}`, {
      method: "POST",
      body: formData,
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data?.message || data?.error || "Failed to upload file");
    }
    return data;
  } catch (error) {
    throw new Error(error?.message || "Failed to upload file");
  }
};

export const fetchRecentItems = async (adminId) => {
  try {
    const response = await erpFetch(`/recentLostItem/${adminId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (response.ok) {
      const data = await response.json();
      return data;
    } else {
      throw new Error("Failed to fetch timetable");
    }
  } catch (error) {
    throw new Error("Error fetching recentLostItem: " + error.message);
  }
};

export const claimItem = async (itemId, Reqdata) => {
  try {
    const response = await erpFetch(`/claimItem/${itemId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(Reqdata),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(
        data?.message || data?.error || "Failed to send claim request",
      );
    }
    return data;
  } catch (error) {
    throw new Error(error?.message || "Failed to send claim request");
  }
};

export const approve_claimItem = async (itemId, stuId) => {
  try {
    const response = await erpFetch(`/approve_claimItem/${itemId}/${stuId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(
        data?.message || data?.error || "Failed to approve claim request",
      );
    }
    return data;
  } catch (error) {
    throw new Error(error?.message || "Failed to approve claim request");
  }
};

export const decline_claimItem = async (itemId, stuId) => {
  try {
    const response = await erpFetch(`/decline_claimItem/${itemId}/${stuId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(
        data?.message || data?.error || "Failed to decline claim request",
      );
    }
    return data;
  } catch (error) {
    throw new Error(error?.message || "Failed to decline claim request");
  }
};

export const deleteLostItem = async (id) => {
  const res = await erpFetch(`/deleteLostItem/${id}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (!res.ok) {
    throw new Error("Failed to delete lost item");
  }
  const ans = await res.json();
  return ans;
};

export const markLostItemReturned = async (id, payload = {}) => {
  const res = await erpFetch(`/returnLostItem/${id}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error("Failed to mark item as returned");
  }
  const ans = await res.json();
  return ans;
};

export const requestPasswordResetForManagement = async (payload = {}) => {
  const schoolId = String(payload.schoolId || payload.username || "").trim();
  if (!schoolId) {
    throw new Error("Login ID is required.");
  }

  const body = {
    schoolId,
    schoolCode: String(payload.schoolCode || "").trim(),
    contactNumber: String(payload.contactNumber || "").trim(),
    parentName: String(payload.parentName || "").trim(),
    reason: String(payload.reason || "").trim(),
    role: "parent",
    source: "erp_frontend",
  };

  const response = await publicFetch("/password-reset/request", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const parsed = await parseJsonSafe(response);
  if (response.ok) {
    return { ...parsed, submitted: true };
  }
  throw buildStatusError(response, parsed, "Failed to create reset request.");
};

const changeOwnPassword = async (payload = {}) => {
  const currentPassword = String(
    payload.currentPassword || payload.oldPassword || "",
  ).trim();
  const newPassword = String(payload.newPassword || "").trim();

  if (!currentPassword) {
    throw new Error("Current password is required.");
  }
  if (!newPassword) {
    throw new Error("New password is required.");
  }

  const body = {
    currentPassword,
    newPassword,
  };
  const response = await erpFetch("/change-password", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const parsed = await parseJsonSafe(response);
  if (!response.ok) {
    throw buildStatusError(response, parsed, "Failed to change password.");
  }
  return parsed;
};

export const changeParentPassword = (payload = {}) =>
  changeOwnPassword(payload);

export const changeTeacherPassword = (payload = {}) =>
  changeOwnPassword(payload);

export const changeManagementPassword = (payload = {}) =>
  changeOwnPassword(payload);

export const changeTitle = async (id, title) => {
  try {
    const res = await erpFetch(`/title/${id}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ title }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data?.message || data?.error || "Failed to update title");
    }
    return data;
  } catch (error) {
    throw new Error(error?.message || "Failed to update title");
  }
};

export const updateLogo = async (id, file) => {
  try {
    const res = await erpFetch(`/uploadLogo/${id}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(file),
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data?.message || data?.error || "Failed to update logo");
    }
    return data;
  } catch (error) {
    throw new Error(error?.message || "Failed to update logo");
  }
};

export const schoolNameLogo = async (id) => {
  const res = await erpFetch(`/api/settings/${encodeURIComponent(id)}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    suppressGlobalError: true,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to load school settings",
    );
  }
  return ans;
};

export const getRolePermissions = async (schoolId) => {
  const query = buildQuery({ schoolId });
  const res = await erpFetch(`/api/settings/role-permissions${query}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    suppressGlobalError: true,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to load role permissions",
    );
  }
  return ans;
};

export const updateRolePermissions = async (
  schoolId,
  roleKey,
  payload = {},
) => {
  const res = await erpFetch(
    `/api/settings/role-permissions/${encodeURIComponent(roleKey)}`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ schoolId, ...payload }),
      suppressGlobalError: true,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to save role permissions",
    );
  }
  return ans;
};

export const resetRolePermissions = async (
  schoolId,
  roleKey,
  expectedRevision,
) => {
  const res = await erpFetch(
    `/api/settings/role-permissions/${encodeURIComponent(roleKey)}/reset`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ schoolId, expectedRevision }),
      suppressGlobalError: true,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw new Error(
      ans?.message || ans?.error || "Failed to reset role permissions",
    );
  }
  return ans;
};

export const initializeManagementPositions = async (schoolId) => {
  const res = await erpFetch("/api/settings/management-positions/initialize", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ schoolId }),
    suppressGlobalError: true,
  });
  const body = await parseJsonSafe(res);
  if (!res.ok)
    throw new Error(body?.message || "Unable to initialize positions.");
  return body;
};

export const all_teachers_names = async (adminId) => {
  const id = String(adminId || "").trim();
  if (!id) return { teachers: [], unavailable: true };
  const res = await erpFetch(`/teachernames/${encodeURIComponent(id)}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    suppressGlobalError: true,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    console.warn("Teacher name lookup failed", {
      status: res.status,
      adminId: id,
      details: ans,
    });
    return { teachers: [], unavailable: true };
  }
  return ans;
};

export const getFeeHeads = async (adminId) => {
  const res = await erpFetch(`/api/fees/heads/${adminId}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
  const ans = await res.json().catch(() => ({}));
  if (!res.ok)
    throw new Error(ans?.message || ans?.error || "Failed to fetch fee heads");
  return ans;
};

export const createFeeHead = async (adminId, payload) => {
  const res = await erpFetch(`/api/fees/heads/${adminId}`, {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      "Content-Type": "application/json",
    },
  });
  const ans = await res.json().catch(() => ({}));
  if (!res.ok)
    throw new Error(ans?.message || ans?.error || "Failed to create fee head");
  return ans;
};

export const updateFeeHead = async (adminId, headId, payload) => {
  const res = await erpFetch(`/api/fees/heads/${adminId}/${headId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
    headers: {
      "Content-Type": "application/json",
    },
  });
  const ans = await res.json().catch(() => ({}));
  if (!res.ok)
    throw new Error(ans?.message || ans?.error || "Failed to update fee head");
  return ans;
};

export const getFeeStructures = async (adminId, params = {}) => {
  const res = await erpFetch(
    `/api/fees/structures/${adminId}${buildQuery(params)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );
  const ans = await res.json().catch(() => ({}));
  if (!res.ok)
    throw new Error(
      ans?.message || ans?.error || "Failed to fetch fee structures",
    );
  return ans;
};

export const upsertFeeStructures = async (adminId, payload) => {
  const res = await erpFetch(`/api/fees/structures/${adminId}`, {
    method: "PUT",
    body: JSON.stringify(payload),
    headers: {
      "Content-Type": "application/json",
    },
  });
  const ans = await res.json().catch(() => ({}));
  if (!res.ok)
    throw new Error(
      ans?.message || ans?.error || "Failed to save fee structures",
    );
  return ans;
};

export const getFeeClassTemplate = async (adminId, params = {}) => {
  const res = await erpFetch(
    `/api/fees/class-template/${adminId}${buildQuery(params)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw new Error(
      ans?.message || ans?.error || "Failed to fetch class template",
    );
  return ans;
};

export const upsertFeeClassTemplate = async (adminId, payload = {}) => {
  const res = await erpFetch(`/api/fees/class-template/${adminId}`, {
    method: "PUT",
    body: JSON.stringify(payload),
    headers: {
      "Content-Type": "application/json",
    },
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw new Error(
      ans?.message || ans?.error || "Failed to save class template",
    );
  return ans;
};

export const getFeeDiscounts = async (adminId, params = {}) => {
  const res = await erpFetch(
    `/api/fees/discounts/${adminId}${buildQuery(params)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );
  const ans = await res.json().catch(() => ({}));
  if (!res.ok)
    throw new Error(
      ans?.message || ans?.error || "Failed to fetch fee discounts",
    );
  return ans;
};

export const createFeeDiscount = async (adminId, payload) => {
  const res = await erpFetch(`/api/fees/discounts/${adminId}`, {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      "Content-Type": "application/json",
    },
  });
  const ans = await res.json().catch(() => ({}));
  if (!res.ok)
    throw new Error(
      ans?.message || ans?.error || "Failed to create fee discount",
    );
  return ans;
};

export const reviewFeeDiscount = async (adminId, discountId, payload) => {
  const res = await erpFetch(
    `/api/fees/discounts/${adminId}/${discountId}/review`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );
  const ans = await res.json().catch(() => ({}));
  if (!res.ok)
    throw new Error(
      ans?.message || ans?.error || "Failed to review fee discount",
    );
  return ans;
};

export const getFeeConfig = async (adminId, session) => {
  const res = await erpFetch(
    `/api/fees/config/${adminId}/${encodeURIComponent(session)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );
  const ans = await res.json();
  if (!res.ok)
    throw new Error(ans?.message || ans?.error || "Failed to fetch fee config");
  return ans;
};

export const updateFeeConfig = async (adminId, session, payload) => {
  const res = await erpFetch(
    `/api/fees/config/${adminId}/${encodeURIComponent(session)}`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );
  const ans = await res.json();
  if (!res.ok)
    throw new Error(
      ans?.message || ans?.error || "Failed to update fee config",
    );
  return ans;
};

export const getFeeMasterLock = async (adminId, session) => {
  const res = await erpFetch(
    `/api/fees/master-lock/${adminId}/${encodeURIComponent(session)}`,
    { method: "GET" },
  );
  const ans = await res.json().catch(() => ({}));
  if (!res.ok)
    throw new Error(
      ans?.message || ans?.error || "Failed to fetch Fee Master lock",
    );
  return ans;
};

export const setFeeMasterLock = async (adminId, session, payload) => {
  const res = await erpFetch(
    `/api/fees/master-lock/${adminId}/${encodeURIComponent(session)}`,
    {
      method: "PUT",
      body: JSON.stringify(payload),
      headers: { "Content-Type": "application/json" },
    },
  );
  const ans = await res.json().catch(() => ({}));
  if (!res.ok)
    throw new Error(
      ans?.message || ans?.error || "Failed to update Fee Master lock",
    );
  return ans;
};

export const generateFeeBills = async (adminId, payload) => {
  const res = await erpFetch(`/api/fees/bills/generate/${adminId}`, {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      "Content-Type": "application/json",
    },
  });
  const ans = await res.json().catch(() => ({}));
  if (!res.ok)
    throw new Error(
      ans?.message || ans?.error || "Failed to generate fee bills",
    );
  return ans;
};

export const previewBatchInvoices = async (adminId, payload = {}) => {
  const res = await erpFetch(`/api/fees/invoices/batch/preview`, {
    method: "POST",
    body: JSON.stringify({
      ...payload,
      adminId,
    }),
    headers: {
      "Content-Type": "application/json",
    },
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw new Error(
      ans?.message || ans?.error || "Failed to preview batch invoices",
    );
  return ans;
};

export const createBatchInvoices = async (adminId, payload = {}) => {
  const res = await erpFetch(`/api/fees/invoices/batch/create`, {
    method: "POST",
    body: JSON.stringify({
      ...payload,
      adminId,
    }),
    headers: {
      "Content-Type": "application/json",
    },
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw new Error(
      ans?.message || ans?.error || "Failed to create batch invoices",
    );
  return ans;
};

export const listInvoices = async (adminId, params = {}) => {
  const res = await erpFetch(
    `/api/fees/invoices${buildQuery({ ...params, adminId })}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    const error = new Error(
      ans?.message || ans?.error || "Failed to fetch invoices",
    );
    error.code = ans?.code || "";
    error.status = res.status;
    error.payload = ans;
    throw error;
  }
  return ans;
};

export const updateInvoice = async (adminId, invoiceId, payload = {}) => {
  const id = String(invoiceId || "").trim();
  if (!id) throw new Error("invoiceId is required");
  const res = await erpFetch(`/api/fees/invoices/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify({
      ...payload,
      adminId,
    }),
    headers: {
      "Content-Type": "application/json",
    },
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    const error = new Error(
      ans?.message || ans?.error || "Failed to update invoice",
    );
    error.code = ans?.code || "";
    error.status = res.status;
    error.payload = ans;
    throw error;
  }
  return ans;
};

export const publishInvoice = async (adminId, invoiceId, payload = {}) => {
  const id = String(invoiceId || "").trim();
  if (!id) throw new Error("invoiceId is required");
  const res = await erpFetch(
    `/api/fees/invoices/${encodeURIComponent(id)}/publish`,
    {
      method: "POST",
      body: JSON.stringify({
        ...payload,
        adminId,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    const error = new Error(
      ans?.message || ans?.error || "Failed to publish invoice",
    );
    error.code = ans?.code || "";
    error.status = res.status;
    error.payload = ans;
    throw error;
  }
  return ans;
};

export const getBatchInvoiceReport = async (adminId, jobId) => {
  const id = String(jobId || "").trim();
  if (!id) throw new Error("jobId is required");
  const res = await erpFetch(
    `/api/fees/invoices/batch/${encodeURIComponent(id)}/report${buildQuery({ adminId })}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw new Error(
      ans?.message || ans?.error || "Failed to load batch report",
    );
  return ans;
};

export const voidInvoice = async (adminId, invoiceId, payload = {}) => {
  const id = String(invoiceId || "").trim();
  if (!id) throw new Error("invoiceId is required");
  const res = await erpFetch(
    `/api/fees/invoices/${encodeURIComponent(id)}/void`,
    {
      method: "POST",
      body: JSON.stringify({
        ...payload,
        adminId,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw new Error(ans?.message || ans?.error || "Failed to void invoice");
  return ans;
};

export const deleteInvoice = async (adminId, invoiceId, payload = {}) => {
  const id = String(invoiceId || "").trim();
  if (!id) throw new Error("invoiceId is required");
  const res = await erpFetch(
    `/api/fees/invoices/${encodeURIComponent(id)}${buildQuery({ adminId })}`,
    {
      method: "DELETE",
      body: JSON.stringify({
        ...payload,
        adminId,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    const error = new Error(
      ans?.message || ans?.error || "Failed to delete invoice",
    );
    error.code = ans?.code || "";
    error.status = res.status;
    error.payload = ans;
    throw error;
  }
  return ans;
};

export const applyFeeAdjustment = async (adminId, payload = {}) => {
  const res = await erpFetch(`/api/fees/adjustments/${adminId}`, {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      "Content-Type": "application/json",
    },
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw new Error(
      ans?.message || ans?.error || "Failed to apply fee adjustment",
    );
  return ans;
};

export const publishFeeBills = async (adminId, payload) => {
  const res = await erpFetch(`/api/fees/bills/publish/${adminId}`, {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      "Content-Type": "application/json",
    },
  });
  const ans = await res.json().catch(() => ({}));
  if (!res.ok)
    throw new Error(
      ans?.message || ans?.error || "Failed to publish fee bills",
    );
  return ans;
};

export const listFeeBills = async (adminId, params = {}, options = {}) => {
  const {
    suppressGlobalError: suppressFromParams,
    optionalStatuses: optionalFromParams,
    allowNotFound: allowNotFoundFromParams,
    ...queryParams
  } = params || {};
  const requestOptions = {
    suppressGlobalError: options?.suppressGlobalError ?? suppressFromParams,
    optionalStatuses: options?.optionalStatuses ?? optionalFromParams,
    allowNotFound: options?.allowNotFound ?? allowNotFoundFromParams,
  };
  const res = await erpFetch(
    `/api/fees/bills/${adminId}${buildQuery(queryParams)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      suppressGlobalError: requestOptions.suppressGlobalError,
    },
  );
  const ans = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (isOptionalStatus(res, requestOptions))
      return { rows: [], bills: [], count: 0 };
    throw new Error(ans?.message || ans?.error || "Failed to fetch fee bills");
  }
  return ans;
};

export const getFeeBill = async (adminId, billId) => {
  const res = await erpFetch(`/api/fees/bills/${adminId}/${billId}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
  const ans = await res.json().catch(() => ({}));
  if (!res.ok)
    throw new Error(ans?.message || ans?.error || "Failed to fetch fee bill");
  return ans;
};

export const getFeeBillPrint = async (adminId, billId) => {
  const res = await erpFetch(`/api/fees/bills/${adminId}/${billId}/print`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
  const ans = await res.json().catch(() => ({}));
  if (!res.ok)
    throw new Error(
      ans?.message || ans?.error || "Failed to fetch printable fee bill",
    );
  return ans;
};

export const cancelFeeBill = async (adminId, billId, payload) => {
  const res = await erpFetch(`/api/fees/bills/${adminId}/${billId}/cancel`, {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      "Content-Type": "application/json",
    },
  });
  const ans = await res.json().catch(() => ({}));
  if (!res.ok)
    throw new Error(ans?.message || ans?.error || "Failed to cancel fee bill");
  return ans;
};

export const reissueFeeBill = async (adminId, billId, payload) => {
  const res = await erpFetch(`/api/fees/bills/${adminId}/${billId}/reissue`, {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      "Content-Type": "application/json",
    },
  });
  const ans = await res.json().catch(() => ({}));
  if (!res.ok)
    throw new Error(ans?.message || ans?.error || "Failed to reissue fee bill");
  return ans;
};

export const overrideBillLateFee = async (adminId, payload) => {
  const res = await erpFetch(`/api/fees/bills/late-fee/override/${adminId}`, {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      "Content-Type": "application/json",
    },
  });
  const ans = await res.json().catch(() => ({}));
  if (!res.ok)
    throw new Error(
      ans?.message || ans?.error || "Failed to override late fee",
    );
  return ans;
};

export const collectFee = async (adminId, payload) => {
  const res = await erpFetch(`/api/fees/collect/${adminId}`, {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      "Content-Type": "application/json",
    },
    suppressGlobalError: true,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to record collection",
    );
  }
  return ans;
};

export const encashPdcReceipt = async (receiptId, payload = {}) => {
  const id = String(receiptId || "").trim();
  if (!id) throw new Error("receiptId is required");
  const res = await erpFetch(`/api/fees/pdc/${encodeURIComponent(id)}/encash`, {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      "Content-Type": "application/json",
    },
    suppressGlobalError: true,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to mark cheque deposited",
    );
  }
  return ans;
};

export const clearChequeReceipt = async (receiptId, payload = {}) => {
  const id = String(receiptId || "").trim();
  if (!id) throw new Error("receiptId is required");
  const res = await erpFetch(
    `/api/fees/cheque/${encodeURIComponent(id)}/clear`,
    {
      method: "POST",
      body: JSON.stringify(payload),
      headers: {
        "Content-Type": "application/json",
      },
      suppressGlobalError: true,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to mark cheque deposited",
    );
  }
  return ans;
};

export const markFeeInstrumentDeposited = async (receiptId, payload = {}) => {
  const id = String(receiptId || "").trim();
  if (!id) throw new Error("receiptId is required");
  const res = await erpFetch(
    `/api/fees/instruments/${encodeURIComponent(id)}/deposit`,
    {
      method: "POST",
      body: JSON.stringify(payload),
      headers: {
        "Content-Type": "application/json",
      },
      suppressGlobalError: true,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to mark cheque deposited",
    );
  }
  return ans;
};

export const bounceFeeInstrument = async (receiptId, payload = {}) => {
  const id = String(receiptId || "").trim();
  if (!id) throw new Error("receiptId is required");
  const res = await erpFetch(
    `/api/fees/instruments/${encodeURIComponent(id)}/bounce`,
    {
      method: "POST",
      body: JSON.stringify(payload),
      headers: {
        "Content-Type": "application/json",
      },
      suppressGlobalError: true,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to mark cheque bounced",
    );
  }
  return ans;
};

export const cancelFeeInstrument = async (receiptId, payload = {}) => {
  const id = String(receiptId || "").trim();
  if (!id) throw new Error("receiptId is required");
  const res = await erpFetch(
    `/api/fees/instruments/${encodeURIComponent(id)}/cancel`,
    {
      method: "POST",
      body: JSON.stringify(payload),
      headers: {
        "Content-Type": "application/json",
      },
      suppressGlobalError: true,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to cancel cheque/PDC",
    );
  }
  return ans;
};

export const getFeeReceiptsRegister = async (
  adminId,
  params = {},
  options = {},
) => {
  const res = await erpFetch(
    `/api/fees/receipts/${adminId}${buildQuery(params)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      suppressGlobalError: options?.suppressGlobalError,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    if (isOptionalStatus(res, options)) return { rows: [], count: 0 };
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to fetch receipts register",
    );
  }
  return ans;
};

export const getFeeReceipt = async (adminId, receiptId) => {
  const res = await erpFetch(
    `/api/fees/receipts/${encodeURIComponent(adminId)}/${encodeURIComponent(receiptId)}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to fetch receipt",
    );
  return ans;
};

export const correctFinanceInvoice = async (
  adminId,
  invoiceId,
  payload = {},
) => {
  const res = await erpFetch(
    `/api/finance/corrections/invoices/${encodeURIComponent(invoiceId)}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ adminId, ...payload }),
      suppressGlobalError: true,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || "Failed to correct invoice",
    );
  return ans;
};

export const correctFinanceReceipt = async (
  adminId,
  receiptId,
  payload = {},
) => {
  const res = await erpFetch(
    `/api/finance/corrections/receipts/${encodeURIComponent(receiptId)}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ adminId, ...payload }),
      suppressGlobalError: true,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || "Failed to correct receipt",
    );
  return ans;
};

export const getFinanceDocumentCorrections = async (
  adminId,
  entityType,
  entityId,
) => {
  const res = await erpFetch(
    `/api/finance/corrections/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}?adminId=${encodeURIComponent(adminId)}`,
    { method: "GET", suppressGlobalError: true },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || "Failed to load correction history",
    );
  return ans;
};

export const getFeeBankCashRegister = async (
  adminId,
  params = {},
  options = {},
) => {
  const res = await erpFetch(
    `/api/fees/bank-cash/${adminId}${buildQuery(params)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      suppressGlobalError: options?.suppressGlobalError,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    if (isOptionalStatus(res, options))
      return { rows: [], count: 0, summary: {} };
    throw buildStatusError(
      res,
      ans,
      ans?.message || ans?.error || "Failed to fetch Bank/Cash register",
    );
  }
  return ans;
};

// =========================
// Finance (double-entry)
// =========================

export const fetchFinanceAccounts = async (adminId, options = {}) => {
  const res = await erpFetch(
    `/api/finance/accounts?adminId=${encodeURIComponent(adminId)}`,
    {
      method: "GET",
      suppressGlobalError: options?.suppressGlobalError,
    },
  );
  const ans = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (isOptionalStatus(res, options)) return { accounts: [] };
    throw new Error(
      ans?.message || ans?.error || "Failed to fetch finance accounts",
    );
  }
  return ans;
};

export const createFinanceAccount = async (adminId, payload) => {
  const res = await erpFetch("/api/finance/accounts", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...payload, adminId }),
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || "Failed to create finance account",
    );
  return ans;
};

export const updateFinanceAccount = async (adminId, accountId, payload) => {
  const res = await erpFetch(
    `/api/finance/accounts/${encodeURIComponent(accountId)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...payload, adminId }),
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || "Failed to update finance account",
    );
  return ans;
};

export const getFormalAccountingReport = async (
  adminId,
  reportType,
  params = {},
) => {
  const res = await erpFetch(
    `/api/finance/formal-report/${encodeURIComponent(reportType)}${buildQuery({ adminId, ...params })}`,
    { method: "GET" },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok)
    throw buildStatusError(
      res,
      ans,
      ans?.message || "Failed to load accounting report",
    );
  return ans;
};

export const exportFormalAccountingReportCsv = async (
  adminId,
  reportType,
  params = {},
) => {
  const res = await erpFetch(
    `/api/finance/formal-report/${encodeURIComponent(reportType)}${buildQuery({ adminId, ...params, format: "csv" })}`,
    { method: "GET" },
  );
  if (!res.ok) {
    const ans = await parseJsonSafe(res);
    throw buildStatusError(
      res,
      ans,
      ans?.message || "Failed to export accounting report",
    );
  }
  return res.text();
};

export const fetchFinanceBalances = async (adminId, asOf) => {
  const qs = new URLSearchParams({ adminId, asOf: asOf || "" });
  const res = await erpFetch(`/api/finance/balances?${qs.toString()}`, {
    method: "GET",
  });
  const ans = await res.json().catch(() => ({}));
  if (!res.ok)
    throw new Error(ans?.message || ans?.error || "Failed to fetch balances");
  return ans;
};

export const adjustLateFee = async (adminId, payload) => {
  const res = await erpFetch(`/api/fees/late-fee/adjust/${adminId}`, {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      "Content-Type": "application/json",
    },
  });
  const ans = await res.json();
  if (!res.ok)
    throw new Error(ans?.message || ans?.error || "Failed to adjust late fee");
  return ans;
};

export const refundFee = async (adminId, payload) => {
  const res = await erpFetch(`/api/fees/refund/${adminId}`, {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      "Content-Type": "application/json",
    },
  });
  const ans = await res.json();
  if (!res.ok)
    throw new Error(ans?.message || ans?.error || "Failed to record refund");
  return ans;
};

export const reverseFeeEntry = async (adminId, payload) => {
  const res = await erpFetch(`/api/fees/reversal/${adminId}`, {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      "Content-Type": "application/json",
    },
  });
  const ans = await res.json();
  if (!res.ok)
    throw new Error(
      ans?.message || ans?.error || "Failed to create reversal entry",
    );
  return ans;
};

export const getSchoolLedger = async (
  adminId,
  sessionOrParams = "",
  options = {},
) => {
  const params =
    typeof sessionOrParams === "string"
      ? { session: sessionOrParams }
      : sessionOrParams && typeof sessionOrParams === "object"
        ? sessionOrParams
        : {};
  const res = await erpFetch(
    `/api/fees/school-ledger/${adminId}${buildQuery(params)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      suppressGlobalError: options?.suppressGlobalError,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    if (isOptionalStatus(res, options)) return null;
    throw buildStatusError(res, ans, "Failed to fetch school ledger");
  }
  return ans;
};

export const exportSchoolLedgerCsv = async (adminId, params = {}) => {
  const res = await erpFetch(
    `/api/fees/school-ledger/${adminId}${buildQuery({ ...params, format: "csv" })}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "text/csv",
      },
    },
  );
  const text = await res.text();
  if (!res.ok) {
    throw new Error(text || "Failed to export school ledger");
  }
  return text;
};

export const getStudentLedger = async (
  adminId,
  studentId,
  session = "",
  options = {},
) => {
  const res = await erpFetch(
    `/api/fees/student-ledger/${adminId}/${studentId}${buildQuery({ session })}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      suppressGlobalError: options?.suppressGlobalError,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    if (isOptionalStatus(res, options)) {
      return { available: false, yearWise: {}, lifetime: null, entries: [] };
    }
    throw buildStatusError(res, ans, "Failed to fetch student ledger");
  }
  return ans;
};

export const getPendingFeesReport = async (
  adminId,
  params = {},
  options = {},
) => {
  const res = await erpFetch(
    `/api/fees/reports/pending/${adminId}${buildQuery(params)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      suppressGlobalError: options?.suppressGlobalError,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    if (isOptionalStatus(res, options)) return null;
    throw buildStatusError(res, ans, "Failed to fetch pending fees report");
  }
  return ans;
};

export const exportPendingFeesCsv = async (adminId, params = {}) => {
  const res = await erpFetch(
    `/api/fees/reports/pending/${adminId}${buildQuery({ ...params, format: "csv" })}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "text/csv",
      },
    },
  );
  const text = await res.text();
  if (!res.ok) {
    throw new Error(text || "Failed to export pending fees CSV");
  }
  return text;
};

export const getCollectionReport = async (
  adminId,
  params = {},
  options = {},
) => {
  const res = await erpFetch(
    `/api/fees/reports/collections/${adminId}${buildQuery(params)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      suppressGlobalError: options?.suppressGlobalError,
    },
  );
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    if (isOptionalStatus(res, options)) return null;
    throw buildStatusError(res, ans, "Failed to fetch collection report");
  }
  return ans;
};

export const exportCollectionReportCsv = async (adminId, params = {}) => {
  const res = await erpFetch(
    `/api/fees/reports/collections/${adminId}${buildQuery({ ...params, format: "csv" })}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "text/csv",
      },
    },
  );
  const text = await res.text();
  if (!res.ok) {
    throw new Error(text || "Failed to export collection report CSV");
  }
  return text;
};

export const getFeeReconciliation = async (adminId, params = {}) => {
  const res = await erpFetch(
    `/api/fees/reconciliation/${adminId}${buildQuery(params)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      suppressGlobalError: true,
    },
  );
  const ans = await res.json().catch(() => ({}));
  if (!res.ok)
    throw new Error(
      ans?.message || ans?.error || "Failed to fetch reconciliation report",
    );
  return ans;
};

export const getStudentFeeStatement = async (
  adminId,
  studentId,
  params = {},
  options = {},
) => {
  const {
    suppressGlobalError: suppressFromParams,
    optionalStatuses: optionalFromParams,
    allowNotFound: allowNotFoundFromParams,
    ...queryParams
  } = params || {};
  const requestOptions = {
    suppressGlobalError: options?.suppressGlobalError ?? suppressFromParams,
    optionalStatuses: options?.optionalStatuses ?? optionalFromParams,
    allowNotFound: options?.allowNotFound ?? allowNotFoundFromParams,
  };
  const res = await erpFetch(
    `/api/fees/reports/student-statement/${adminId}/${studentId}${buildQuery(queryParams)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      suppressGlobalError: requestOptions.suppressGlobalError,
    },
  );
  const ans = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (isOptionalStatus(res, requestOptions)) return { rows: [] };
    throw new Error(
      ans?.message || ans?.error || "Failed to fetch student fee statement",
    );
  }
  return ans;
};

export const getStudentReceipts = async (studentId, options = {}) => {
  const res = await erpFetch(`/api/fees/student/${studentId}/receipts`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    suppressGlobalError: options?.suppressGlobalError,
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    if (isOptionalStatus(res, options)) return [];
    throw buildStatusError(res, ans, "Failed to fetch receipts");
  }
  return ans;
};

// Minimal Accounting API aliases (frontend compatibility layer)
export const createFeeInvoice = async (adminId, payload = {}) => {
  const res = await erpFetch(`/api/fees/invoices`, {
    method: "POST",
    body: JSON.stringify({
      ...payload,
      adminId,
    }),
    headers: {
      "Content-Type": "application/json",
    },
  });
  const ans = await parseJsonSafe(res);
  if (!res.ok) {
    const error = new Error(
      ans?.message || ans?.error || "Failed to create invoice",
    );
    error.code = ans?.code || "";
    error.status = res.status;
    error.payload = ans;
    throw error;
  }
  return ans;
};

export const searchAccountingStudents = async (adminId, query, options = {}) =>
  searchFeeStudents(adminId, query, options);

export const getStudentFeeAccount = async (adminId, studentId, options = {}) =>
  getFeeStudentDue(adminId, studentId, options);

export const getStudentAccountStatement = async (
  adminId,
  studentId,
  params = {},
) => {
  try {
    return await getStudentFeeStatement(adminId, studentId, params);
  } catch (error) {
    const ledger = await getStudentLedger(
      adminId,
      studentId,
      params?.session || "",
      {
        suppressGlobalError: true,
        optionalStatuses: [404],
      },
    );
    return { rows: Array.isArray(ledger?.entries) ? ledger.entries : [] };
  }
};

export const recordStudentPayment = async (adminId, payload = {}) =>
  collectFee(adminId, payload);

export const getStudentReceiptPdf = async (studentId, options = {}) =>
  getStudentReceipts(studentId, options);
